// Shared translations utility for the entire app

export const dashboardTranslations = {
  en: {
    // Common
    logout: "Logout",
    signOut: "Sign Out",
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    submit: "Submit",
    delete: "Delete",
    edit: "Edit",
    view: "View",
    search: "Search",
    filter: "Filter",
    status: "Status",
    actions: "Actions",
    noData: "No data available",
    back: "Back",
    submitting: "Submitting...",
    uploading: "Uploading...",
    selected: "Selected",
    uploaded: "Uploaded",
    verified: "Verified",
    pendingVerification: "Pending Verification",
    escalated: "Escalated",
    due: "Due",
    // Status
    status: {
      active: "Active",
      pending: "Pending"
    },
    
    // Categories
    categories: {
      general: "General",
      workplace: "Workplace",
      salary: "Salary",
      harassment: "Harassment",
      safety: "Safety",
      other: "Other"
    },
    
    // Priorities
    priority: {
      low: "Low",
      medium: "Medium",
      high: "High",
      critical: "Critical"
    },
    
    // Member Dashboard
    member: {
      title: "MyShield",
      subtitle: "Worker Protection Platform",
      nav: {
        profile: "Profile",
        complaints: "Complaints",
        payments: "Payments",
        documents: "Documents",
        messages: "Messages",
        settings: "Settings",
        announcements: "Announcements",
        sos: "SOS / Emergency",
        vacancies: "Vacancies"
      },
      profile: {
        title: "My Profile",
        personalInfo: "Personal Information",
        employmentDetails: "Employment Details",
        fullName: "Full Name",
        email: "Email",
        phone: "Phone",
        passportNumber: "Passport Number",
        nationality: "Nationality",
        employer: "Employer",
        visaExpiry: "Visa Expiry",
        subscriptionStatus: "Subscription Status",
        monthlyFee: "Monthly Fee",
        updateProfile: "Update Profile"
      },
      complaints: {
        title: "My Complaints",
        newComplaint: "New Complaint",
        complaintTitle: "Title",
        description: "Description",
        category: "Category",
        priority: "Priority",
        submitComplaint: "Submit Complaint",
        noComplaints: "No complaints submitted yet",
        submitDescription: "Describe your issue and we'll get back to you soon",
        titlePlaceholder: "Brief description of the issue",
        descriptionPlaceholder: "Provide detailed information about your complaint",
        selectCategory: "Select category",
        selectPriority: "Select priority"
      },
      payments: {
        title: "Payment History",
        makePayment: "Make Payment",
        myPayments: "My Payments",
        manageSubscription: "Manage your subscription and payments",
        monthlySubscription: "Monthly Subscription",
        chooseMethod: "Choose your preferred payment method",
        cardPayment: "Card Payment",
        cardPaymentDesc: "One-time payment with debit/credit card",
        autoDebit: "Auto-Debit Monthly",
        autoDebitDesc: "Automatic monthly payment - cancel anytime",
        bankTransfer: "Bank Transfer",
        bankTransferDesc: "Manual transfer to bank account",
        qrDesc: "Scan and pay with any banking app",
        bankDetails: "Bank Account Details",
        bank: "Bank",
        accountName: "Account Name",
        accountNumber: "Account Number",
        afterPayment: "After payment, enter details below:",
        afterTransfer: "After transfer, enter details below:",
        referenceNumber: "Reference Number",
        enterReference: "Enter transaction reference",
        submitProof: "Submit Payment Proof",
        noHistory: "No payment history yet",
        amount: "Amount",
        date: "Date",
        method: "Method",
        qrPayment: "QR Code Payment",
        bankTransferPayment: "Bank Transfer Payment"
      },
      documents: {
        title: "My Documents",
        uploadDocument: "Upload Document",
        documentType: "Document Type",
        passport: "Passport",
        visa: "Visa/Work Permit",
        contract: "Employment Contract",
        other: "Other",
        noDocuments: "No documents uploaded yet",
        uploadPrompt: "Upload your passport, work permit, or employment contract",
        uploadDesc: "Upload your important documents securely. Formats: PDF, JPG, PNG (max 5MB)",
        selectType: "Select document type",
        selectFile: "Select File"
      },
      chat: {
        title: "Messages",
        sendMessage: "Send a message...",
        online: "Online",
        offline: "Offline",
        lastSeen: "Last seen",
        noChat: "No active chat available",
        noChatDesc: "You will be able to chat once a staff member is assigned to your case."
      },
      settings: {
        title: "Settings",
        language: "Language",
        languageDesc: "Choose your preferred language for the dashboard",
        accountInfo: "Account Information",
        email: "Email",
        memberId: "Member ID",
        accountStatus: "Account Status"
      },
      announcements: {
        title: "Announcements",
        tabLabel: "Announcements",
        noAnnouncements: "No announcements available",
        readMore: "Read More",
        shareWhatsApp: "Share on WhatsApp",
        pinned: "Pinned",
        postedOn: "Posted on",
        videoAwareness: "Video Awareness",
        videoAwarenessDesc: "Important videos about worker rights and safety",
        workerRights: "Worker Rights",
        tutorials: "Tutorials",
        safetyVideos: "Safety Videos",
        legalAid: "Legal Aid",
        allAnnouncements: "All Announcements",
        categories: {
          warning: "Warning",
          info: "Information",
          update: "Update",
          video: "Video"
        },
        backToList: "Back to Announcements",
        watchVideo: "Watch Video",
        viewImage: "View Image",
        noVideos: "No videos available yet"
      },
      sos: {
        title: "SOS / Emergency",
        sendSOS: "SEND SOS",
        holdToSend: "Hold for 3 seconds to send SOS",
        confirmTitle: "Confirm SOS Alert",
        confirmMessage: "Are you sure you want to send an emergency SOS alert? This will immediately notify MyShield administrators of your emergency situation.",
        confirmYes: "Yes, Send SOS",
        confirmNo: "Cancel",
        successTitle: "SOS Alert Sent",
        successMessage: "Your emergency alert has been sent. Our team will contact you as soon as possible.",
        emergencyContacts: "Emergency Contacts",
        myshieldHotline: "MyShield Hotline",
        labourDept: "Labour Department (JTKSM)",
        police: "Police",
        ambulance: "Ambulance",
        sosHistory: "SOS History",
        noHistory: "No previous SOS alerts",
        status: {
          pending: "Pending",
          in_progress: "In Progress",
          resolved: "Resolved"
        },
        stayCalm: "Stay calm and safe. Help is on the way.",
        callDirectly: "Call directly for immediate assistance"
      },
      vacancies: {
        title: "Job Vacancies",
        searchPlaceholder: "Search jobs...",
        filters: "Filters",
        allIndustries: "All Industries",
        allLocations: "All Locations",
        allTypes: "All Types",
        salaryRange: "Salary Range",
        clearFilters: "Clear Filters",
        jobType: {
          full_time: "Full Time",
          part_time: "Part Time",
          contract: "Contract",
          temporary: "Temporary"
        },
        perMonth: "per month",
        postedOn: "Posted on",
        deadline: "Deadline",
        apply: "Apply",
        applied: "Applied",
        viewDetails: "View Details",
        requirements: "Requirements",
        description: "Description",
        applyNow: "Apply Now",
        applicationSent: "Application Sent!",
        alreadyApplied: "You have already applied",
        backToJobs: "Back to Jobs",
        myApplications: "My Applications",
        noJobs: "No job vacancies available",
        noApplications: "You haven't applied for any jobs yet",
        coverLetter: "Cover Letter (Optional)",
        coverLetterPlaceholder: "Write a brief introduction about yourself...",
        confirmApply: "Confirm Application",
        confirmApplyMessage: "Are you sure you want to apply for this position?",
        applicationStatus: {
          submitted: "Submitted",
          reviewing: "Under Review",
          interview: "Interview",
          accepted: "Accepted",
          rejected: "Not Selected"
        },
        companyName: "Company",
        location: "Location",
        salary: "Salary",
        applicants: "applicants"
      }
    },

    // Staff Dashboard
    staff: {
      title: "MyShield Staff",
      subtitle: "Worker Management",
      nav: {
        myCases: "My Cases",
        messages: "Messages"
      },
      cases: {
        title: "Assigned Cases",
        worker: "Worker",
        assignedDate: "Assigned Date",
        noCases: "No cases assigned"
      }
    },

    // Admin Dashboard
    admin: {
      title: "Admin Portal",
      nav: {
        dashboard: "Dashboard",
        visaTracker: "Visa Tracker",
        chatOversight: "Chat Oversight",
        members: "Members",
        staffManagement: "Staff Management",
        reports: "Reports",
        analytics: "Analytics",
        announcements: "Announcements"
      },
      dashboard: {
        welcome: "Welcome back",
        totalWorkers: "Total Workers",
        activeComplaints: "Active Complaints",
        totalStaff: "Total Staff",
        monthlyRevenue: "Monthly Revenue"
      },
      members: {
        title: "All Members",
        addMember: "Add Member",
        viewProfile: "View Profile"
      },
      visaTracker: {
        title: "Visa & Permit Tracker",
        expiringSoon: "Expiring Soon",
        expired: "Expired",
        valid: "Valid",
        sendAlerts: "Send Alerts"
      },
      reports: {
        title: "Reports",
        generate: "Generate Report",
        download: "Download",
        workers: "Workers Report",
        complaints: "Complaints Report",
        payments: "Payments Report"
      },
      announcements: {
        title: "Announcements Management",
        createNew: "Create Announcement",
        editAnnouncement: "Edit Announcement",
        deleteAnnouncement: "Delete Announcement",
        confirmDelete: "Are you sure you want to delete this announcement?",
        titleLabel: "Title",
        bodyLabel: "Content",
        categoryLabel: "Category",
        imageLabel: "Image (Optional)",
        videoLabel: "Video URL (YouTube or Upload)",
        expiryLabel: "Expiry Date (Optional)",
        pinAnnouncement: "Pin Announcement",
        unpinAnnouncement: "Unpin",
        publish: "Publish",
        unpublish: "Unpublish",
        published: "Published",
        draft: "Draft",
        expired: "Expired",
        readCount: "Views",
        uploadMedia: "Upload Media",
        uploading: "Uploading...",
        youtubeUrl: "YouTube URL",
        pasteYoutubeUrl: "Paste YouTube video URL",
        orUploadVideo: "Or upload video file",
        categories: {
          warning: "Warning",
          info: "Information",
          update: "Update",
          video: "Video"
        },
        noAnnouncements: "No announcements created yet",
        createFirst: "Create your first announcement to keep workers informed"
      },
      jobs: {
        title: "Jobs Management",
        subtitle: "Create and manage job postings for workers",
        createJob: "Create Job Posting",
        editJob: "Edit Job Posting",
        deleteJob: "Delete Job",
        confirmDelete: "Are you sure you want to delete this job? All applications will also be deleted.",
        jobTitle: "Job Title",
        companyName: "Company Name",
        location: "Location",
        salaryMin: "Minimum Salary (RM)",
        salaryMax: "Maximum Salary (RM)",
        jobType: "Job Type",
        industry: "Industry",
        description: "Job Description",
        requirements: "Requirements",
        applicationDeadline: "Application Deadline",
        published: "Published",
        unpublished: "Unpublished",
        expired: "Expired",
        publish: "Publish",
        unpublish: "Unpublish",
        noJobs: "No job postings yet",
        createFirst: "Create your first job posting for workers to apply",
        applicants: "Applicants",
        viewApplicants: "View Applicants",
        noApplicants: "No applicants yet for this position",
        allApplications: "All Applications",
        applicantName: "Applicant",
        jobPosition: "Job Position",
        appliedOn: "Applied On",
        coverLetter: "Cover Letter",
        updateStatus: "Update Status",
        statusUpdated: "Application status updated!",
        jobTypes: {
          full_time: "Full Time",
          part_time: "Part Time",
          contract: "Contract",
          temporary: "Temporary"
        },
        industries: {
          general: "General",
          construction: "Construction",
          manufacturing: "Manufacturing",
          agriculture: "Agriculture",
          hospitality: "Hospitality",
          services: "Services",
          logistics: "Logistics",
          retail: "Retail"
        },
        applicationStatus: {
          submitted: "Submitted",
          reviewing: "Reviewing",
          interview: "Interview",
          accepted: "Accepted",
          rejected: "Rejected"
        }
      }
    }
  },

  ms: {
    logout: "Log Keluar",
    signOut: "Log Keluar",
    loading: "Memuatkan...",
    save: "Simpan",
    cancel: "Batal",
    submit: "Hantar",
    delete: "Padam",
    edit: "Edit",
    view: "Lihat",
    search: "Cari",
    filter: "Tapis",
    status: "Status",
    actions: "Tindakan",
    noData: "Tiada data",
    back: "Kembali",
    submitting: "Menghantar...",
    uploading: "Memuat naik...",
    selected: "Dipilih",
    uploaded: "Dimuat naik",
    verified: "Disahkan",
    pendingVerification: "Menunggu Pengesahan",
    escalated: "Ditingkatkan",
    due: "Tarikh Akhir",
    
    // Status
    status: {
      active: "Aktif",
      pending: "Menunggu"
    },
    
    // Categories
    categories: {
      general: "Umum",
      workplace: "Tempat Kerja",
      salary: "Gaji",
      harassment: "Gangguan",
      safety: "Keselamatan",
      other: "Lain-lain"
    },
    
    // Priorities
    priority: {
      low: "Rendah",
      medium: "Sederhana",
      high: "Tinggi",
      critical: "Kritikal"
    },

    member: {
      title: "MyShield",
      subtitle: "Platform Perlindungan Pekerja",
      nav: {
        profile: "Profil",
        complaints: "Aduan",
        payments: "Pembayaran",
        documents: "Dokumen",
        messages: "Mesej",
        settings: "Tetapan",
        announcements: "Pengumuman",
        sos: "SOS / Kecemasan",
        vacancies: "Kerja"
      },
      profile: {
        title: "Profil Saya",
        personalInfo: "Maklumat Peribadi",
        employmentDetails: "Maklumat Pekerjaan",
        fullName: "Nama Penuh",
        email: "Emel",
        phone: "Telefon",
        passportNumber: "Nombor Pasport",
        nationality: "Kewarganegaraan",
        employer: "Majikan",
        visaExpiry: "Tamat Visa",
        subscriptionStatus: "Status Langganan",
        monthlyFee: "Yuran Bulanan",
        updateProfile: "Kemaskini Profil"
      },
      complaints: {
        title: "Aduan Saya",
        newComplaint: "Aduan Baru",
        complaintTitle: "Tajuk",
        description: "Penerangan",
        category: "Kategori",
        priority: "Keutamaan",
        submitComplaint: "Hantar Aduan",
        noComplaints: "Tiada aduan dihantar lagi",
        submitDescription: "Terangkan masalah anda dan kami akan menghubungi anda",
        titlePlaceholder: "Penerangan ringkas masalah",
        descriptionPlaceholder: "Berikan maklumat terperinci tentang aduan anda",
        selectCategory: "Pilih kategori",
        selectPriority: "Pilih keutamaan"
      },
      payments: {
        title: "Sejarah Pembayaran",
        makePayment: "Buat Pembayaran",
        myPayments: "Pembayaran Saya",
        manageSubscription: "Urus langganan dan pembayaran anda",
        monthlySubscription: "Langganan Bulanan",
        chooseMethod: "Pilih kaedah pembayaran pilihan anda",
        cardPayment: "Pembayaran Kad",
        cardPaymentDesc: "Bayaran sekali gus dengan kad debit/kredit",
        autoDebit: "Auto-Debit Bulanan",
        autoDebitDesc: "Pembayaran bulanan automatik - batal bila-bila masa",
        bankTransfer: "Pindahan Bank",
        bankTransferDesc: "Pindahan manual ke akaun bank",
        qrDesc: "Imbas dan bayar dengan mana-mana aplikasi perbankan",
        bankDetails: "Maklumat Akaun Bank",
        bank: "Bank",
        accountName: "Nama Akaun",
        accountNumber: "Nombor Akaun",
        afterPayment: "Selepas pembayaran, masukkan maklumat di bawah:",
        afterTransfer: "Selepas pindahan, masukkan maklumat di bawah:",
        referenceNumber: "Nombor Rujukan",
        enterReference: "Masukkan rujukan transaksi",
        submitProof: "Hantar Bukti Pembayaran",
        noHistory: "Tiada sejarah pembayaran lagi",
        amount: "Jumlah",
        date: "Tarikh",
        method: "Kaedah",
        qrPayment: "Pembayaran Kod QR",
        bankTransferPayment: "Pembayaran Pindahan Bank"
      },
      documents: {
        title: "Dokumen Saya",
        uploadDocument: "Muat Naik Dokumen",
        documentType: "Jenis Dokumen",
        passport: "Pasport",
        visa: "Visa/Permit Kerja",
        contract: "Kontrak Pekerjaan",
        other: "Lain-lain",
        noDocuments: "Tiada dokumen dimuat naik lagi",
        uploadPrompt: "Muat naik pasport, permit kerja, atau kontrak pekerjaan anda",
        uploadDesc: "Muat naik dokumen penting anda dengan selamat. Format: PDF, JPG, PNG (maks 5MB)",
        selectType: "Pilih jenis dokumen",
        selectFile: "Pilih Fail"
      },
      chat: {
        title: "Mesej",
        sendMessage: "Hantar mesej...",
        online: "Dalam Talian",
        offline: "Luar Talian",
        lastSeen: "Terakhir dilihat",
        noChat: "Tiada chat aktif",
        noChatDesc: "Anda boleh chat selepas kakitangan ditugaskan kepada kes anda."
      },
      settings: {
        title: "Tetapan",
        language: "Bahasa",
        languageDesc: "Pilih bahasa pilihan anda untuk papan pemuka",
        accountInfo: "Maklumat Akaun",
        email: "Emel",
        memberId: "ID Ahli",
        accountStatus: "Status Akaun"
      },
      announcements: {
        title: "Pengumuman",
        tabLabel: "Pengumuman",
        noAnnouncements: "Tiada pengumuman tersedia",
        readMore: "Baca Lagi",
        shareWhatsApp: "Kongsi di WhatsApp",
        pinned: "Disematkan",
        postedOn: "Disiarkan pada",
        videoAwareness: "Video Kesedaran",
        videoAwarenessDesc: "Video penting tentang hak pekerja dan keselamatan",
        workerRights: "Hak Pekerja",
        tutorials: "Tutorial",
        safetyVideos: "Video Keselamatan",
        legalAid: "Bantuan Undang-undang",
        allAnnouncements: "Semua Pengumuman",
        categories: {
          warning: "Amaran",
          info: "Maklumat",
          update: "Kemaskini",
          video: "Video"
        },
        backToList: "Kembali ke Pengumuman",
        watchVideo: "Tonton Video",
        viewImage: "Lihat Gambar",
        noVideos: "Tiada video tersedia lagi"
      },
      sos: {
        title: "SOS / Kecemasan",
        sendSOS: "HANTAR SOS",
        holdToSend: "Tekan 3 saat untuk hantar SOS",
        confirmTitle: "Sahkan Amaran SOS",
        confirmMessage: "Adakah anda pasti mahu menghantar amaran kecemasan SOS? Ini akan segera memberitahu pentadbir MyShield tentang situasi kecemasan anda.",
        confirmYes: "Ya, Hantar SOS",
        confirmNo: "Batal",
        successTitle: "Amaran SOS Dihantar",
        successMessage: "Amaran kecemasan anda telah dihantar. Pasukan kami akan menghubungi anda secepat mungkin.",
        emergencyContacts: "Nombor Kecemasan",
        myshieldHotline: "Talian Hotline MyShield",
        labourDept: "Jabatan Tenaga Kerja (JTKSM)",
        police: "Polis",
        ambulance: "Ambulans",
        sosHistory: "Sejarah SOS",
        noHistory: "Tiada amaran SOS sebelumnya",
        status: {
          pending: "Menunggu",
          in_progress: "Dalam Proses",
          resolved: "Selesai"
        },
        stayCalm: "Kekal tenang dan selamat. Bantuan dalam perjalanan.",
        callDirectly: "Hubungi terus untuk bantuan segera"
      },
      vacancies: {
        title: "Kekosongan Jawatan",
        searchPlaceholder: "Cari kerja...",
        filters: "Penapis",
        allIndustries: "Semua Industri",
        allLocations: "Semua Lokasi",
        allTypes: "Semua Jenis",
        salaryRange: "Julat Gaji",
        clearFilters: "Kosongkan Penapis",
        jobType: {
          full_time: "Sepenuh Masa",
          part_time: "Separuh Masa",
          contract: "Kontrak",
          temporary: "Sementara"
        },
        perMonth: "sebulan",
        postedOn: "Disiarkan pada",
        deadline: "Tarikh Tutup",
        apply: "Mohon",
        applied: "Telah Mohon",
        viewDetails: "Lihat Butiran",
        requirements: "Keperluan",
        description: "Penerangan",
        applyNow: "Mohon Sekarang",
        applicationSent: "Permohonan Dihantar!",
        alreadyApplied: "Anda sudah memohon",
        backToJobs: "Kembali ke Senarai Kerja",
        myApplications: "Permohonan Saya",
        noJobs: "Tiada kekosongan jawatan tersedia",
        noApplications: "Anda belum memohon sebarang kerja",
        coverLetter: "Surat Iringan (Pilihan)",
        coverLetterPlaceholder: "Tulis pengenalan ringkas tentang diri anda...",
        confirmApply: "Sahkan Permohonan",
        confirmApplyMessage: "Adakah anda pasti mahu memohon jawatan ini?",
        applicationStatus: {
          submitted: "Dihantar",
          reviewing: "Dalam Semakan",
          interview: "Temuduga",
          accepted: "Berjaya",
          rejected: "Tidak Berjaya"
        },
        companyName: "Syarikat",
        location: "Lokasi",
        salary: "Gaji",
        applicants: "pemohon"
      }
    },

    staff: {
      title: "Kakitangan MyShield",
      subtitle: "Pengurusan Pekerja",
      nav: {
        myCases: "Kes Saya",
        messages: "Mesej"
      },
      cases: {
        title: "Kes Ditugaskan",
        worker: "Pekerja",
        assignedDate: "Tarikh Ditugaskan",
        noCases: "Tiada kes ditugaskan"
      }
    },

    admin: {
      title: "Portal Admin",
      nav: {
        dashboard: "Papan Pemuka",
        visaTracker: "Penjejak Visa",
        chatOversight: "Pengawasan Chat",
        members: "Ahli",
        staffManagement: "Pengurusan Kakitangan",
        reports: "Laporan",
        analytics: "Analitik",
        announcements: "Pengumuman"
      },
      dashboard: {
        welcome: "Selamat kembali",
        totalWorkers: "Jumlah Pekerja",
        activeComplaints: "Aduan Aktif",
        totalStaff: "Jumlah Kakitangan",
        monthlyRevenue: "Pendapatan Bulanan"
      },
      members: {
        title: "Semua Ahli",
        addMember: "Tambah Ahli",
        viewProfile: "Lihat Profil"
      },
      visaTracker: {
        title: "Penjejak Visa & Permit",
        expiringSoon: "Hampir Tamat",
        expired: "Tamat Tempoh",
        valid: "Sah",
        sendAlerts: "Hantar Amaran"
      },
      reports: {
        title: "Laporan",
        generate: "Jana Laporan",
        download: "Muat Turun",
        workers: "Laporan Pekerja",
        complaints: "Laporan Aduan",
        payments: "Laporan Pembayaran"
      },
      announcements: {
        title: "Pengurusan Pengumuman",
        createNew: "Buat Pengumuman",
        editAnnouncement: "Edit Pengumuman",
        deleteAnnouncement: "Padam Pengumuman",
        confirmDelete: "Adakah anda pasti mahu memadam pengumuman ini?",
        titleLabel: "Tajuk",
        bodyLabel: "Kandungan",
        categoryLabel: "Kategori",
        imageLabel: "Gambar (Pilihan)",
        videoLabel: "URL Video (YouTube atau Muat Naik)",
        expiryLabel: "Tarikh Tamat (Pilihan)",
        pinAnnouncement: "Sematkan Pengumuman",
        unpinAnnouncement: "Nyahsemat",
        publish: "Terbitkan",
        unpublish: "Nyahterbit",
        published: "Diterbitkan",
        draft: "Draf",
        expired: "Tamat Tempoh",
        readCount: "Tontonan",
        uploadMedia: "Muat Naik Media",
        uploading: "Memuat naik...",
        youtubeUrl: "URL YouTube",
        pasteYoutubeUrl: "Tampal URL video YouTube",
        orUploadVideo: "Atau muat naik fail video",
        categories: {
          warning: "Amaran",
          info: "Maklumat",
          update: "Kemaskini",
          video: "Video"
        },
        noAnnouncements: "Tiada pengumuman dicipta lagi",
        createFirst: "Buat pengumuman pertama anda untuk memaklumkan pekerja"
      },
      jobs: {
        title: "Pengurusan Kerja",
        subtitle: "Buat dan urus jawatan kerja untuk pekerja",
        createJob: "Buat Iklan Kerja",
        editJob: "Edit Iklan Kerja",
        deleteJob: "Padam Kerja",
        confirmDelete: "Adakah anda pasti mahu memadam kerja ini? Semua permohonan juga akan dipadam.",
        jobTitle: "Jawatan Kerja",
        companyName: "Nama Syarikat",
        location: "Lokasi",
        salaryMin: "Gaji Minimum (RM)",
        salaryMax: "Gaji Maksimum (RM)",
        jobType: "Jenis Kerja",
        industry: "Industri",
        description: "Penerangan Kerja",
        requirements: "Keperluan",
        applicationDeadline: "Tarikh Akhir Permohonan",
        published: "Diterbitkan",
        unpublished: "Tidak Diterbitkan",
        expired: "Tamat Tempoh",
        publish: "Terbitkan",
        unpublish: "Nyahterbit",
        noJobs: "Tiada iklan kerja lagi",
        createFirst: "Buat iklan kerja pertama anda untuk pekerja memohon",
        applicants: "Pemohon",
        viewApplicants: "Lihat Pemohon",
        noApplicants: "Tiada pemohon lagi untuk jawatan ini",
        allApplications: "Semua Permohonan",
        applicantName: "Pemohon",
        jobPosition: "Jawatan Kerja",
        appliedOn: "Tarikh Permohonan",
        coverLetter: "Surat Permohonan",
        updateStatus: "Kemaskini Status",
        statusUpdated: "Status permohonan dikemaskini!",
        jobTypes: {
          full_time: "Sepenuh Masa",
          part_time: "Separuh Masa",
          contract: "Kontrak",
          temporary: "Sementara"
        },
        industries: {
          general: "Umum",
          construction: "Pembinaan",
          manufacturing: "Pembuatan",
          agriculture: "Pertanian",
          hospitality: "Hospitaliti",
          services: "Perkhidmatan",
          logistics: "Logistik",
          retail: "Runcit"
        },
        applicationStatus: {
          submitted: "Dihantar",
          reviewing: "Dalam Semakan",
          interview: "Temuduga",
          accepted: "Diterima",
          rejected: "Ditolak"
        }
      }
    }
  },

  ta: {
    logout: "வெளியேறு",
    signOut: "வெளியேறு",
    loading: "ஏற்றுகிறது...",
    save: "சேமி",
    cancel: "ரத்து",
    submit: "சமர்ப்பி",
    delete: "நீக்கு",
    edit: "திருத்து",
    view: "பார்",
    search: "தேடு",
    filter: "வடிகட்டு",
    status: "நிலை",
    actions: "செயல்கள்",
    noData: "தரவு இல்லை",
    back: "திரும்பு",
    submitting: "சமர்ப்பிக்கிறது...",
    uploading: "பதிவேற்றுகிறது...",
    selected: "தேர்ந்தெடுக்கப்பட்டது",
    uploaded: "பதிவேற்றப்பட்டது",
    verified: "சரிபார்க்கப்பட்டது",
    pendingVerification: "சரிபார்ப்பு நிலுவையில்",
    escalated: "அதிகரிக்கப்பட்டது",
    due: "நிலுவை",
    
    // Status
    status: {
      active: "செயலில்",
      pending: "நிலுவையில்"
    },
    
    // Categories
    categories: {
      general: "பொது",
      workplace: "பணியிடம்",
      salary: "சம்பளம்",
      harassment: "தொல்லை",
      safety: "பாதுகாப்பு",
      other: "மற்றவை"
    },
    
    // Priorities
    priority: {
      low: "குறைவு",
      medium: "நடுத்தரம்",
      high: "அதிகம்",
      critical: "முக்கியமான"
    },

    member: {
      title: "MyShield",
      subtitle: "தொழிலாளர் பாதுகாப்பு தளம்",
      nav: {
        profile: "சுயவிவரம்",
        complaints: "புகார்கள்",
        payments: "கொடுப்பனவுகள்",
        documents: "ஆவணங்கள்",
        messages: "செய்திகள்",
        settings: "அமைப்புகள்",
        announcements: "அறிவிப்புகள்",
        sos: "SOS / அவசரநிலை",
        vacancies: "வேலைவாய்ப்புகள்"
      },
      profile: {
        title: "என் சுயவிவரம்",
        personalInfo: "தனிப்பட்ட தகவல்",
        employmentDetails: "வேலை விவரங்கள்",
        fullName: "முழு பெயர்",
        email: "மின்னஞ்சல்",
        phone: "தொலைபேசி",
        passportNumber: "கடவுச்சீட்டு எண்",
        nationality: "தேசியம்",
        employer: "முதலாளி",
        visaExpiry: "விசா காலாவதி",
        subscriptionStatus: "சந்தா நிலை",
        monthlyFee: "மாதாந்திர கட்டணம்",
        updateProfile: "சுயவிவரத்தைப் புதுப்பி"
      },
      complaints: {
        title: "என் புகார்கள்",
        newComplaint: "புதிய புகார்",
        complaintTitle: "தலைப்பு",
        description: "விளக்கம்",
        category: "வகை",
        priority: "முன்னுரிமை",
        submitComplaint: "புகார் சமர்ப்பி",
        noComplaints: "இன்னும் புகார்கள் இல்லை",
        submitDescription: "உங்கள் பிரச்சனையை விவரிக்கவும், விரைவில் பதிலளிப்போம்",
        titlePlaceholder: "பிரச்சனையின் சுருக்கமான விளக்கம்",
        descriptionPlaceholder: "உங்கள் புகார் பற்றிய விரிவான தகவல் கொடுங்கள்",
        selectCategory: "வகையைத் தேர்ந்தெடுங்கள்",
        selectPriority: "முன்னுரிமையைத் தேர்ந்தெடுங்கள்"
      },
      payments: {
        title: "கொடுப்பனவு வரலாறு",
        makePayment: "கொடுப்பனவு செய்",
        myPayments: "என் கொடுப்பனவுகள்",
        manageSubscription: "உங்கள் சந்தா மற்றும் கொடுப்பனவுகளை நிர்வகிக்கவும்",
        monthlySubscription: "மாதாந்திர சந்தா",
        chooseMethod: "உங்கள் விருப்பமான கொடுப்பனவு முறையைத் தேர்ந்தெடுங்கள்",
        cardPayment: "கார்டு கொடுப்பனவு",
        cardPaymentDesc: "டெபிட்/கிரெடிட் கார்டுடன் ஒரு முறை கொடுப்பனவு",
        autoDebit: "தானியங்கி மாதாந்திர கொடுப்பனவு",
        autoDebitDesc: "தானியங்கி மாதாந்திர கொடுப்பனவு - எப்போது வேண்டுமானாலும் ரத்து செய்யலாம்",
        bankTransfer: "வங்கி பரிமாற்றம்",
        bankTransferDesc: "வங்கி கணக்கிற்கு கைமுறை பரிமாற்றம்",
        qrDesc: "எந்த வங்கி செயலியிலும் ஸ்கேன் செய்து செலுத்துங்கள்",
        bankDetails: "வங்கி கணக்கு விவரங்கள்",
        bank: "வங்கி",
        accountName: "கணக்கு பெயர்",
        accountNumber: "கணக்கு எண்",
        afterPayment: "கொடுப்பனவுக்குப் பிறகு, கீழே விவரங்களை உள்ளிடவும்:",
        afterTransfer: "பரிமாற்றத்திற்குப் பிறகு, கீழே விவரங்களை உள்ளிடவும்:",
        referenceNumber: "குறிப்பு எண்",
        enterReference: "பரிவர்த்தனை குறிப்பை உள்ளிடவும்",
        submitProof: "கொடுப்பனவு சான்று சமர்ப்பிக்கவும்",
        noHistory: "இன்னும் கொடுப்பனவு வரலாறு இல்லை",
        amount: "தொகை",
        date: "தேதி",
        method: "முறை",
        qrPayment: "QR குறியீடு கொடுப்பனவு",
        bankTransferPayment: "வங்கி பரிமாற்ற கொடுப்பனவு"
      },
      documents: {
        title: "என் ஆவணங்கள்",
        uploadDocument: "ஆவணத்தைப் பதிவேற்று",
        documentType: "ஆவண வகை",
        passport: "கடவுச்சீட்டு",
        visa: "விசா/வேலை அனுமதி",
        contract: "வேலை ஒப்பந்தம்",
        other: "மற்றவை",
        noDocuments: "இன்னும் ஆவணங்கள் பதிவேற்றப்படவில்லை",
        uploadPrompt: "உங்கள் கடவுச்சீட்டு, வேலை அனுமதி அல்லது வேலை ஒப்பந்தத்தை பதிவேற்றவும்",
        uploadDesc: "உங்கள் முக்கியமான ஆவணங்களை பாதுகாப்பாக பதிவேற்றவும். வடிவங்கள்: PDF, JPG, PNG (அதிகபட்சம் 5MB)",
        selectType: "ஆவண வகையைத் தேர்ந்தெடுங்கள்",
        selectFile: "கோப்பைத் தேர்ந்தெடுங்கள்"
      },
      chat: {
        title: "செய்திகள்",
        sendMessage: "செய்தி அனுப்பு...",
        online: "ஆன்லைன்",
        offline: "ஆஃப்லைன்",
        lastSeen: "கடைசியாக பார்த்தது",
        noChat: "செயலில் அரட்டை இல்லை",
        noChatDesc: "உங்கள் வழக்கிற்கு ஒரு ஊழியர் நியமிக்கப்பட்டவுடன் நீங்கள் அரட்டை அடிக்க முடியும்."
      },
      settings: {
        title: "அமைப்புகள்",
        language: "மொழி",
        languageDesc: "டாஷ்போர்டுக்கான உங்கள் விருப்பமான மொழியைத் தேர்ந்தெடுங்கள்",
        accountInfo: "கணக்கு தகவல்",
        email: "மின்னஞ்சல்",
        memberId: "உறுப்பினர் ஐடி",
        accountStatus: "கணக்கு நிலை"
      },
      announcements: {
        title: "அறிவிப்புகள்",
        tabLabel: "அறிவிப்புகள்",
        noAnnouncements: "அறிவிப்புகள் இல்லை",
        readMore: "மேலும் படிக்க",
        shareWhatsApp: "WhatsApp-இல் பகிர்",
        pinned: "பின் செய்யப்பட்டது",
        postedOn: "வெளியிடப்பட்ட தேதி",
        videoAwareness: "விழிப்புணர்வு வீடியோக்கள்",
        videoAwarenessDesc: "தொழிலாளர் உரிமைகள் மற்றும் பாதுகாப்பு பற்றிய முக்கிய வீடியோக்கள்",
        workerRights: "தொழிலாளர் உரிமைகள்",
        tutorials: "டுடோரியல்கள்",
        safetyVideos: "பாதுகாப்பு வீடியோக்கள்",
        legalAid: "சட்ட உதவி",
        allAnnouncements: "அனைத்து அறிவிப்புகள்",
        categories: {
          warning: "எச்சரிக்கை",
          info: "தகவல்",
          update: "புதுப்பிப்பு",
          video: "வீடியோ"
        },
        backToList: "அறிவிப்புகளுக்குத் திரும்பு",
        watchVideo: "வீடியோ பார்",
        viewImage: "படம் பார்",
        noVideos: "இன்னும் வீடியோக்கள் இல்லை"
      },
      sos: {
        title: "SOS / அவசரநிலை",
        sendSOS: "SOS அனுப்பு",
        holdToSend: "SOS அனுப்ப 3 விநாடிகள் அழுத்தவும்",
        confirmTitle: "SOS எச்சரிக்கையை உறுதிப்படுத்து",
        confirmMessage: "அவசர SOS எச்சரிக்கையை அனுப்ப விரும்புகிறீர்களா? இது உடனடியாக MyShield நிர்வாகிகளுக்கு உங்கள் அவசர நிலையைத் தெரிவிக்கும்.",
        confirmYes: "ஆம், SOS அனுப்பு",
        confirmNo: "ரத்து செய்",
        successTitle: "SOS எச்சரிக்கை அனுப்பப்பட்டது",
        successMessage: "உங்கள் அவசர எச்சரிக்கை அனுப்பப்பட்டது. எங்கள் குழு விரைவில் உங்களைத் தொடர்பு கொள்ளும்.",
        emergencyContacts: "அவசர தொடர்புகள்",
        myshieldHotline: "MyShield ஹாட்லைன்",
        labourDept: "தொழிலாளர் திணைக்களம் (JTKSM)",
        police: "காவல்துறை",
        ambulance: "ஆம்புலன்ஸ்",
        sosHistory: "SOS வரலாறு",
        noHistory: "முந்தைய SOS எச்சரிக்கைகள் இல்லை",
        status: {
          pending: "நிலுவையில்",
          in_progress: "செயல்பாட்டில்",
          resolved: "தீர்க்கப்பட்டது"
        },
        stayCalm: "அமைதியாகவும் பாதுகாப்பாகவும் இருங்கள். உதவி வருகிறது.",
        callDirectly: "உடனடி உதவிக்கு நேரடியாக அழைக்கவும்"
      },
      vacancies: {
        title: "வேலை காலிப்பணியிடங்கள்",
        searchPlaceholder: "வேலை தேடு...",
        filters: "வடிகட்டிகள்",
        allIndustries: "அனைத்து தொழில்கள்",
        allLocations: "அனைத்து இடங்கள்",
        allTypes: "அனைத்து வகைகள்",
        salaryRange: "சம்பள வரம்பு",
        clearFilters: "வடிகட்டிகளை அழி",
        jobType: {
          full_time: "முழு நேரம்",
          part_time: "பகுதி நேரம்",
          contract: "ஒப்பந்தம்",
          temporary: "தற்காலிக"
        },
        perMonth: "மாதத்திற்கு",
        postedOn: "வெளியிடப்பட்டது",
        deadline: "கடைசி தேதி",
        apply: "விண்ணப்பி",
        applied: "விண்ணப்பித்தது",
        viewDetails: "விவரங்கள் பார்",
        requirements: "தேவைகள்",
        description: "விளக்கம்",
        applyNow: "இப்போது விண்ணப்பி",
        applicationSent: "விண்ணப்பம் அனுப்பப்பட்டது!",
        alreadyApplied: "நீங்கள் ஏற்கனவே விண்ணப்பித்துள்ளீர்கள்",
        backToJobs: "வேலைகளுக்குத் திரும்பு",
        myApplications: "என் விண்ணப்பங்கள்",
        noJobs: "வேலை காலியிடங்கள் இல்லை",
        noApplications: "நீங்கள் இன்னும் எந்த வேலைக்கும் விண்ணப்பிக்கவில்லை",
        coverLetter: "அறிமுக கடிதம் (விரும்பினால்)",
        coverLetterPlaceholder: "உங்களைப் பற்றி சுருக்கமாக எழுதுங்கள்...",
        confirmApply: "விண்ணப்பத்தை உறுதிப்படுத்து",
        confirmApplyMessage: "இந்த பதவிக்கு விண்ணப்பிக்க விரும்புகிறீர்களா?",
        applicationStatus: {
          submitted: "சமர்ப்பிக்கப்பட்டது",
          reviewing: "பரிசீலனையில்",
          interview: "நேர்காணல்",
          accepted: "தேர்வு",
          rejected: "தேர்வாகவில்லை"
        },
        companyName: "நிறுவனம்",
        location: "இடம்",
        salary: "சம்பளம்",
        applicants: "விண்ணப்பதாரர்கள்"
      }
    },

    staff: {
      title: "MyShield ஊழியர்",
      subtitle: "தொழிலாளர் மேலாண்மை",
      nav: {
        myCases: "என் வழக்குகள்",
        messages: "செய்திகள்"
      },
      cases: {
        title: "ஒதுக்கப்பட்ட வழக்குகள்",
        worker: "தொழிலாளி",
        assignedDate: "ஒதுக்கிய தேதி",
        noCases: "வழக்குகள் ஒதுக்கப்படவில்லை"
      }
    },

    admin: {
      title: "நிர்வாக போர்டல்",
      nav: {
        dashboard: "டாஷ்போர்டு",
        visaTracker: "விசா கண்காணிப்பு",
        chatOversight: "அரட்டை மேற்பார்வை",
        members: "உறுப்பினர்கள்",
        staffManagement: "ஊழியர் மேலாண்மை",
        reports: "அறிக்கைகள்",
        analytics: "பகுப்பாய்வு",
        announcements: "அறிவிப்புகள்"
      },
      dashboard: {
        welcome: "மீண்டும் வரவேற்கிறோம்",
        totalWorkers: "மொத்த தொழிலாளர்கள்",
        activeComplaints: "செயலில் உள்ள புகார்கள்",
        totalStaff: "மொத்த ஊழியர்கள்",
        monthlyRevenue: "மாத வருவாய்"
      },
      members: {
        title: "அனைத்து உறுப்பினர்கள்",
        addMember: "உறுப்பினர் சேர்",
        viewProfile: "சுயவிவரம் பார்"
      },
      visaTracker: {
        title: "விசா & அனுமதி கண்காணிப்பு",
        expiringSoon: "விரைவில் காலாவதி",
        expired: "காலாவதியானது",
        valid: "செல்லுபடியாகும்",
        sendAlerts: "எச்சரிக்கைகள் அனுப்பு"
      },
      reports: {
        title: "அறிக்கைகள்",
        generate: "அறிக்கை உருவாக்கு",
        download: "பதிவிறக்கு",
        workers: "தொழிலாளர் அறிக்கை",
        complaints: "புகார் அறிக்கை",
        payments: "கொடுப்பனவு அறிக்கை"
      },
      announcements: {
        title: "அறிவிப்பு மேலாண்மை",
        createNew: "அறிவிப்பு உருவாக்கு",
        editAnnouncement: "அறிவிப்பை திருத்து",
        deleteAnnouncement: "அறிவிப்பை நீக்கு",
        confirmDelete: "இந்த அறிவிப்பை நீக்க விரும்புகிறீர்களா?",
        titleLabel: "தலைப்பு",
        bodyLabel: "உள்ளடக்கம்",
        categoryLabel: "வகை",
        imageLabel: "படம் (விரும்பினால்)",
        videoLabel: "வீடியோ URL (YouTube அல்லது பதிவேற்று)",
        expiryLabel: "காலாவதி தேதி (விரும்பினால்)",
        pinAnnouncement: "அறிவிப்பை பின் செய்",
        unpinAnnouncement: "பின் நீக்கு",
        publish: "வெளியிடு",
        unpublish: "வெளியீட்டை நீக்கு",
        published: "வெளியிடப்பட்டது",
        draft: "வரைவு",
        expired: "காலாவதியானது",
        readCount: "பார்வைகள்",
        uploadMedia: "மீடியா பதிவேற்று",
        uploading: "பதிவேற்றுகிறது...",
        youtubeUrl: "YouTube URL",
        pasteYoutubeUrl: "YouTube வீடியோ URL ஒட்டவும்",
        orUploadVideo: "அல்லது வீடியோ கோப்பை பதிவேற்றவும்",
        categories: {
          warning: "எச்சரிக்கை",
          info: "தகவல்",
          update: "புதுப்பிப்பு",
          video: "வீடியோ"
        },
        noAnnouncements: "இன்னும் அறிவிப்புகள் உருவாக்கப்படவில்லை",
        createFirst: "தொழிலாளர்களை தெரிவிக்க உங்கள் முதல் அறிவிப்பை உருவாக்குங்கள்"
      },
      jobs: {
        title: "வேலை மேலாண்மை",
        subtitle: "தொழிலாளர்களுக்கான வேலை பதிவுகளை உருவாக்கி நிர்வகிக்கவும்",
        createJob: "வேலை பதிவு உருவாக்கு",
        editJob: "வேலை பதிவை திருத்து",
        deleteJob: "வேலையை நீக்கு",
        confirmDelete: "இந்த வேலையை நீக்க விரும்புகிறீர்களா? அனைத்து விண்ணப்பங்களும் நீக்கப்படும்.",
        jobTitle: "வேலை தலைப்பு",
        companyName: "நிறுவன பெயர்",
        location: "இடம்",
        salaryMin: "குறைந்தபட்ச சம்பளம் (RM)",
        salaryMax: "அதிகபட்ச சம்பளம் (RM)",
        jobType: "வேலை வகை",
        industry: "தொழில்துறை",
        description: "வேலை விவரம்",
        requirements: "தேவைகள்",
        applicationDeadline: "விண்ணப்ப கெடு",
        published: "வெளியிடப்பட்டது",
        unpublished: "வெளியிடப்படவில்லை",
        expired: "காலாவதியானது",
        publish: "வெளியிடு",
        unpublish: "வெளியீட்டை நீக்கு",
        noJobs: "இன்னும் வேலை பதிவுகள் இல்லை",
        createFirst: "தொழிலாளர்கள் விண்ணப்பிக்க உங்கள் முதல் வேலை பதிவை உருவாக்குங்கள்",
        applicants: "விண்ணப்பதாரர்கள்",
        viewApplicants: "விண்ணப்பதாரர்களை பார்",
        noApplicants: "இந்த பதவிக்கு இன்னும் விண்ணப்பதாரர்கள் இல்லை",
        allApplications: "அனைத்து விண்ணப்பங்கள்",
        applicantName: "விண்ணப்பதாரர்",
        jobPosition: "வேலை பதவி",
        appliedOn: "விண்ணப்பித்த தேதி",
        coverLetter: "மறைப்பு கடிதம்",
        updateStatus: "நிலையை புதுப்பி",
        statusUpdated: "விண்ணப்ப நிலை புதுப்பிக்கப்பட்டது!",
        jobTypes: {
          full_time: "முழு நேரம்",
          part_time: "பகுதி நேரம்",
          contract: "ஒப்பந்தம்",
          temporary: "தற்காலிக"
        },
        industries: {
          general: "பொது",
          construction: "கட்டுமானம்",
          manufacturing: "உற்பத்தி",
          agriculture: "விவசாயம்",
          hospitality: "விருந்தோம்பல்",
          services: "சேவைகள்",
          logistics: "தளவாடம்",
          retail: "சில்லறை"
        },
        applicationStatus: {
          submitted: "சமர்ப்பிக்கப்பட்டது",
          reviewing: "மதிப்பாய்வு செய்கிறது",
          interview: "நேர்காணல்",
          accepted: "ஏற்றுக்கொள்ளப்பட்டது",
          rejected: "நிராகரிக்கப்பட்டது"
        }
      }
    }
  },

  hi: {
    logout: "लॉग आउट",
    signOut: "साइन आउट",
    loading: "लोड हो रहा है...",
    save: "सहेजें",
    cancel: "रद्द करें",
    submit: "जमा करें",
    delete: "हटाएं",
    edit: "संपादित करें",
    view: "देखें",
    search: "खोजें",
    filter: "फ़िल्टर",
    status: "स्थिति",
    actions: "क्रियाएं",
    noData: "कोई डेटा नहीं",
    back: "वापस",
    submitting: "जमा हो रहा है...",
    uploading: "अपलोड हो रहा है...",
    selected: "चयनित",
    uploaded: "अपलोड किया गया",
    verified: "सत्यापित",
    pendingVerification: "सत्यापन प्रतीक्षित",
    escalated: "आगे बढ़ाया गया",
    due: "नियत तिथि",
    
    // Status
    status: {
      active: "सक्रिय",
      pending: "लंबित"
    },
    
    // Categories
    categories: {
      general: "सामान्य",
      workplace: "कार्यस्थल",
      salary: "वेतन",
      harassment: "उत्पीड़न",
      safety: "सुरक्षा",
      other: "अन्य"
    },
    
    // Priorities
    priority: {
      low: "कम",
      medium: "मध्यम",
      high: "उच्च",
      critical: "गंभीर"
    },

    member: {
      title: "MyShield",
      subtitle: "श्रमिक सुरक्षा मंच",
      nav: {
        profile: "प्रोफ़ाइल",
        complaints: "शिकायतें",
        payments: "भुगतान",
        documents: "दस्तावेज़",
        messages: "संदेश",
        settings: "सेटिंग्स",
        announcements: "घोषणाएं",
        sos: "SOS / आपातकाल",
        vacancies: "नौकरियां"
      },
      profile: {
        title: "मेरी प्रोफ़ाइल",
        personalInfo: "व्यक्तिगत जानकारी",
        employmentDetails: "रोजगार विवरण",
        fullName: "पूरा नाम",
        email: "ईमेल",
        phone: "फ़ोन",
        passportNumber: "पासपोर्ट नंबर",
        nationality: "राष्ट्रीयता",
        employer: "नियोक्ता",
        visaExpiry: "वीज़ा समाप्ति",
        subscriptionStatus: "सदस्यता स्थिति",
        monthlyFee: "मासिक शुल्क",
        updateProfile: "प्रोफ़ाइल अपडेट करें"
      },
      complaints: {
        title: "मेरी शिकायतें",
        newComplaint: "नई शिकायत",
        complaintTitle: "शीर्षक",
        description: "विवरण",
        category: "श्रेणी",
        priority: "प्राथमिकता",
        submitComplaint: "शिकायत जमा करें",
        noComplaints: "अभी तक कोई शिकायत नहीं"
      },
      payments: {
        title: "भुगतान इतिहास",
        makePayment: "भुगतान करें",
        myPayments: "मेरे भुगतान",
        manageSubscription: "अपनी सदस्यता और भुगतान प्रबंधित करें",
        monthlySubscription: "मासिक सदस्यता",
        chooseMethod: "अपनी पसंदीदा भुगतान विधि चुनें",
        cardPayment: "कार्ड भुगतान",
        cardPaymentDesc: "डेबिट/क्रेडिट कार्ड से एकमुश्त भुगतान",
        autoDebit: "स्वचालित मासिक भुगतान",
        autoDebitDesc: "स्वचालित मासिक भुगतान - कभी भी रद्द करें",
        bankTransfer: "बैंक ट्रांसफर",
        bankTransferDesc: "बैंक खाते में मैनुअल ट्रांसफर",
        qrDesc: "किसी भी बैंकिंग ऐप से स्कैन करें और भुगतान करें",
        bankDetails: "बैंक खाता विवरण",
        bank: "बैंक",
        accountName: "खाता नाम",
        accountNumber: "खाता नंबर",
        afterPayment: "भुगतान के बाद, नीचे विवरण दर्ज करें:",
        afterTransfer: "ट्रांसफर के बाद, नीचे विवरण दर्ज करें:",
        referenceNumber: "संदर्भ संख्या",
        enterReference: "लेनदेन संदर्भ दर्ज करें",
        submitProof: "भुगतान प्रमाण जमा करें",
        noHistory: "अभी तक कोई भुगतान इतिहास नहीं",
        amount: "राशि",
        date: "तारीख",
        method: "विधि",
        qrPayment: "QR कोड भुगतान"
      },
      documents: {
        title: "मेरे दस्तावेज़",
        uploadDocument: "दस्तावेज़ अपलोड करें",
        documentType: "दस्तावेज़ प्रकार",
        passport: "पासपोर्ट",
        visa: "वीज़ा/कार्य परमिट",
        contract: "रोजगार अनुबंध",
        other: "अन्य",
        noDocuments: "अभी तक कोई दस्तावेज़ अपलोड नहीं किया गया",
        uploadPrompt: "अपना पासपोर्ट, कार्य परमिट, या रोजगार अनुबंध अपलोड करें"
      },
      chat: {
        title: "संदेश",
        sendMessage: "संदेश भेजें...",
        online: "ऑनलाइन",
        offline: "ऑफ़लाइन",
        lastSeen: "अंतिम बार देखा",
        noChat: "कोई सक्रिय चैट उपलब्ध नहीं",
        noChatDesc: "एक बार कोई स्टाफ सदस्य आपके केस को सौंपा जाएगा, तब आप चैट कर सकते हैं।"
      },
      settings: {
        title: "सेटिंग्स",
        language: "भाषा",
        languageDesc: "डैशबोर्ड के लिए अपनी पसंदीदा भाषा चुनें",
        accountInfo: "खाता जानकारी",
        email: "ईमेल",
        memberId: "सदस्य आईडी",
        accountStatus: "खाता स्थिति"
      },
      announcements: {
        title: "घोषणाएं",
        tabLabel: "घोषणाएं",
        noAnnouncements: "कोई घोषणाएं उपलब्ध नहीं",
        readMore: "और पढ़ें",
        shareWhatsApp: "WhatsApp पर शेयर करें",
        pinned: "पिन किया गया",
        postedOn: "पोस्ट किया गया",
        videoAwareness: "जागरूकता वीडियो",
        videoAwarenessDesc: "श्रमिक अधिकारों और सुरक्षा के बारे में महत्वपूर्ण वीडियो",
        workerRights: "श्रमिक अधिकार",
        tutorials: "ट्यूटोरियल",
        safetyVideos: "सुरक्षा वीडियो",
        legalAid: "कानूनी सहायता",
        allAnnouncements: "सभी घोषणाएं",
        categories: {
          warning: "चेतावनी",
          info: "जानकारी",
          update: "अपडेट",
          video: "वीडियो"
        },
        backToList: "घोषणाओं पर वापस",
        watchVideo: "वीडियो देखें",
        viewImage: "छवि देखें",
        noVideos: "अभी तक कोई वीडियो नहीं"
      },
      sos: {
        title: "SOS / आपातकाल",
        sendSOS: "SOS भेजें",
        holdToSend: "SOS भेजने के लिए 3 सेकंड दबाएं",
        confirmTitle: "SOS अलर्ट की पुष्टि करें",
        confirmMessage: "क्या आप वाकई आपातकालीन SOS अलर्ट भेजना चाहते हैं? यह तुरंत MyShield प्रशासकों को आपकी आपातकालीन स्थिति की सूचना देगा।",
        confirmYes: "हां, SOS भेजें",
        confirmNo: "रद्द करें",
        successTitle: "SOS अलर्ट भेजा गया",
        successMessage: "आपका आपातकालीन अलर्ट भेज दिया गया है। हमारी टीम जल्द से जल्द आपसे संपर्क करेगी।",
        emergencyContacts: "आपातकालीन संपर्क",
        myshieldHotline: "MyShield हॉटलाइन",
        labourDept: "श्रम विभाग (JTKSM)",
        police: "पुलिस",
        ambulance: "एम्बुलेंस",
        sosHistory: "SOS इतिहास",
        noHistory: "कोई पिछले SOS अलर्ट नहीं",
        status: {
          pending: "लंबित",
          in_progress: "प्रगति में",
          resolved: "हल किया गया"
        },
        stayCalm: "शांत और सुरक्षित रहें। मदद रास्ते में है।",
        callDirectly: "तत्काल सहायता के लिए सीधे कॉल करें"
      },
      vacancies: {
        title: "नौकरी रिक्तियां",
        searchPlaceholder: "नौकरी खोजें...",
        filters: "फ़िल्टर",
        allIndustries: "सभी उद्योग",
        allLocations: "सभी स्थान",
        allTypes: "सभी प्रकार",
        salaryRange: "वेतन सीमा",
        clearFilters: "फ़िल्टर साफ़ करें",
        jobType: {
          full_time: "पूर्णकालिक",
          part_time: "अंशकालिक",
          contract: "अनुबंध",
          temporary: "अस्थायी"
        },
        perMonth: "प्रति माह",
        postedOn: "पोस्ट किया गया",
        deadline: "अंतिम तिथि",
        apply: "आवेदन करें",
        applied: "आवेदन किया",
        viewDetails: "विवरण देखें",
        requirements: "आवश्यकताएं",
        description: "विवरण",
        applyNow: "अभी आवेदन करें",
        applicationSent: "आवेदन भेजा गया!",
        alreadyApplied: "आपने पहले ही आवेदन कर दिया है",
        backToJobs: "नौकरियों पर वापस",
        myApplications: "मेरे आवेदन",
        noJobs: "कोई नौकरी रिक्तियां उपलब्ध नहीं",
        noApplications: "आपने अभी तक किसी नौकरी के लिए आवेदन नहीं किया है",
        coverLetter: "कवर लेटर (वैकल्पिक)",
        coverLetterPlaceholder: "अपने बारे में संक्षिप्त परिचय लिखें...",
        confirmApply: "आवेदन की पुष्टि करें",
        confirmApplyMessage: "क्या आप इस पद के लिए आवेदन करना चाहते हैं?",
        applicationStatus: {
          submitted: "जमा किया गया",
          reviewing: "समीक्षाधीन",
          interview: "साक्षात्कार",
          accepted: "स्वीकृत",
          rejected: "चयनित नहीं"
        },
        companyName: "कंपनी",
        location: "स्थान",
        salary: "वेतन",
        applicants: "आवेदक"
      }
    },

    staff: {
      title: "MyShield स्टाफ",
      subtitle: "श्रमिक प्रबंधन",
      nav: {
        myCases: "मेरे केस",
        messages: "संदेश"
      },
      cases: {
        title: "सौंपे गए केस",
        worker: "श्रमिक",
        assignedDate: "सौंपने की तारीख",
        noCases: "कोई केस नहीं सौंपा गया"
      }
    },

    admin: {
      title: "एडमिन पोर्टल",
      nav: {
        dashboard: "डैशबोर्ड",
        visaTracker: "वीज़ा ट्रैकर",
        chatOversight: "चैट निगरानी",
        members: "सदस्य",
        staffManagement: "स्टाफ प्रबंधन",
        reports: "रिपोर्ट",
        analytics: "एनालिटिक्स",
        announcements: "घोषणाएं"
      },
      dashboard: {
        welcome: "वापसी पर स्वागत है",
        totalWorkers: "कुल श्रमिक",
        activeComplaints: "सक्रिय शिकायतें",
        totalStaff: "कुल स्टाफ",
        monthlyRevenue: "मासिक राजस्व"
      },
      members: {
        title: "सभी सदस्य",
        addMember: "सदस्य जोड़ें",
        viewProfile: "प्रोफ़ाइल देखें"
      },
      visaTracker: {
        title: "वीज़ा और परमिट ट्रैकर",
        expiringSoon: "जल्द समाप्त",
        expired: "समाप्त",
        valid: "वैध",
        sendAlerts: "अलर्ट भेजें"
      },
      reports: {
        title: "रिपोर्ट",
        generate: "रिपोर्ट बनाएं",
        download: "डाउनलोड",
        workers: "श्रमिक रिपोर्ट",
        complaints: "शिकायत रिपोर्ट",
        payments: "भुगतान रिपोर्ट"
      },
      announcements: {
        title: "घोषणा प्रबंधन",
        createNew: "घोषणा बनाएं",
        editAnnouncement: "घोषणा संपादित करें",
        deleteAnnouncement: "घोषणा हटाएं",
        confirmDelete: "क्या आप इस घोषणा को हटाना चाहते हैं?",
        titleLabel: "शीर्षक",
        bodyLabel: "सामग्री",
        categoryLabel: "श्रेणी",
        imageLabel: "छवि (वैकल्पिक)",
        videoLabel: "वीडियो URL (YouTube या अपलोड)",
        expiryLabel: "समाप्ति तिथि (वैकल्पिक)",
        pinAnnouncement: "घोषणा पिन करें",
        unpinAnnouncement: "अनपिन",
        publish: "प्रकाशित करें",
        unpublish: "अप्रकाशित करें",
        published: "प्रकाशित",
        draft: "ड्राफ्ट",
        expired: "समाप्त",
        readCount: "दृश्य",
        uploadMedia: "मीडिया अपलोड करें",
        uploading: "अपलोड हो रहा है...",
        youtubeUrl: "YouTube URL",
        pasteYoutubeUrl: "YouTube वीडियो URL पेस्ट करें",
        orUploadVideo: "या वीडियो फ़ाइल अपलोड करें",
        categories: {
          warning: "चेतावनी",
          info: "जानकारी",
          update: "अपडेट",
          video: "वीडियो"
        },
        noAnnouncements: "अभी तक कोई घोषणाएं नहीं बनाई गई",
        createFirst: "श्रमिकों को सूचित करने के लिए अपनी पहली घोषणा बनाएं"
      },
      jobs: {
        title: "नौकरी प्रबंधन",
        subtitle: "श्रमिकों के लिए नौकरी पोस्टिंग बनाएं और प्रबंधित करें",
        createJob: "नौकरी पोस्टिंग बनाएं",
        editJob: "नौकरी पोस्टिंग संपादित करें",
        deleteJob: "नौकरी हटाएं",
        confirmDelete: "क्या आप इस नौकरी को हटाना चाहते हैं? सभी आवेदन भी हटा दिए जाएंगे।",
        jobTitle: "नौकरी शीर्षक",
        companyName: "कंपनी का नाम",
        location: "स्थान",
        salaryMin: "न्यूनतम वेतन (RM)",
        salaryMax: "अधिकतम वेतन (RM)",
        jobType: "नौकरी का प्रकार",
        industry: "उद्योग",
        description: "नौकरी विवरण",
        requirements: "आवश्यकताएं",
        applicationDeadline: "आवेदन की अंतिम तिथि",
        published: "प्रकाशित",
        unpublished: "अप्रकाशित",
        expired: "समाप्त",
        publish: "प्रकाशित करें",
        unpublish: "अप्रकाशित करें",
        noJobs: "अभी तक कोई नौकरी पोस्टिंग नहीं",
        createFirst: "श्रमिकों के आवेदन के लिए अपनी पहली नौकरी पोस्टिंग बनाएं",
        applicants: "आवेदक",
        viewApplicants: "आवेदक देखें",
        noApplicants: "इस पद के लिए अभी तक कोई आवेदक नहीं",
        allApplications: "सभी आवेदन",
        applicantName: "आवेदक",
        jobPosition: "नौकरी पद",
        appliedOn: "आवेदन की तारीख",
        coverLetter: "कवर लेटर",
        updateStatus: "स्थिति अपडेट करें",
        statusUpdated: "आवेदन स्थिति अपडेट हो गई!",
        jobTypes: {
          full_time: "पूर्णकालिक",
          part_time: "अंशकालिक",
          contract: "अनुबंध",
          temporary: "अस्थायी"
        },
        industries: {
          general: "सामान्य",
          construction: "निर्माण",
          manufacturing: "विनिर्माण",
          agriculture: "कृषि",
          hospitality: "आतिथ्य",
          services: "सेवाएं",
          logistics: "लॉजिस्टिक्स",
          retail: "खुदरा"
        },
        applicationStatus: {
          submitted: "जमा किया गया",
          reviewing: "समीक्षा में",
          interview: "साक्षात्कार",
          accepted: "स्वीकृत",
          rejected: "अस्वीकृत"
        }
      }
    }
  },

  bn: {
    logout: "লগ আউট",
    signOut: "সাইন আউট",
    loading: "লোড হচ্ছে...",
    save: "সংরক্ষণ",
    cancel: "বাতিল",
    submit: "জমা দিন",
    delete: "মুছুন",
    edit: "সম্পাদনা",
    view: "দেখুন",
    search: "অনুসন্ধান",
    filter: "ফিল্টার",
    status: "অবস্থা",
    actions: "কর্ম",
    noData: "কোন তথ্য নেই",

    member: {
      title: "MyShield",
      subtitle: "শ্রমিক সুরক্ষা প্ল্যাটফর্ম",
      nav: {
        profile: "প্রোফাইল",
        complaints: "অভিযোগ",
        payments: "পেমেন্ট",
        documents: "নথি",
        messages: "বার্তা",
        settings: "সেটিংস",
        announcements: "ঘোষণা",
        sos: "SOS / জরুরি",
        vacancies: "চাকরি"
      },
      profile: {
        title: "আমার প্রোফাইল",
        personalInfo: "ব্যক্তিগত তথ্য",
        fullName: "পুরো নাম",
        email: "ইমেইল",
        phone: "ফোন",
        passportNumber: "পাসপোর্ট নম্বর",
        nationality: "জাতীয়তা",
        employer: "নিয়োগকর্তা",
        visaExpiry: "ভিসার মেয়াদ",
        subscriptionStatus: "সদস্যতা অবস্থা",
        updateProfile: "প্রোফাইল আপডেট"
      },
      complaints: {
        title: "আমার অভিযোগ",
        newComplaint: "নতুন অভিযোগ",
        complaintTitle: "শিরোনাম",
        description: "বিবরণ",
        category: "বিভাগ",
        priority: "অগ্রাধিকার",
        submitComplaint: "অভিযোগ জমা দিন",
        noComplaints: "এখনো কোন অভিযোগ নেই"
      },
      payments: {
        title: "পেমেন্ট ইতিহাস",
        makePayment: "পেমেন্ট করুন",
        amount: "পরিমাণ",
        date: "তারিখ",
        method: "পদ্ধতি",
        qrPayment: "QR কোড পেমেন্ট"
      },
      documents: {
        title: "আমার নথি",
        uploadDocument: "নথি আপলোড",
        documentType: "নথির ধরন",
        passport: "পাসপোর্ট",
        visa: "ভিসা/কাজের অনুমতি",
        contract: "চাকরির চুক্তি",
        other: "অন্যান্য"
      },
      chat: {
        title: "বার্তা",
        sendMessage: "বার্তা পাঠান...",
        online: "অনলাইন",
        offline: "অফলাইন",
        lastSeen: "সর্বশেষ দেখা"
      },
      settings: {
        title: "সেটিংস",
        language: "ভাষা",
        languageDesc: "ড্যাশবোর্ডের জন্য আপনার পছন্দের ভাষা বেছে নিন",
        accountInfo: "অ্যাকাউন্ট তথ্য",
        email: "ইমেল",
        memberId: "সদস্য আইডি",
        accountStatus: "অ্যাকাউন্ট স্থিতি"
      },
      announcements: {
        title: "ঘোষণা",
        tabLabel: "ঘোষণা",
        noAnnouncements: "কোন ঘোষণা নেই",
        readMore: "আরও পড়ুন",
        shareWhatsApp: "WhatsApp-এ শেয়ার করুন",
        pinned: "পিন করা",
        postedOn: "পোস্ট করা হয়েছে",
        videoAwareness: "সচেতনতা ভিডিও",
        videoAwarenessDesc: "শ্রমিক অধিকার ও নিরাপত্তা সম্পর্কে গুরুত্বপূর্ণ ভিডিও",
        workerRights: "শ্রমিক অধিকার",
        tutorials: "টিউটোরিয়াল",
        safetyVideos: "নিরাপত্তা ভিডিও",
        legalAid: "আইনি সহায়তা",
        allAnnouncements: "সকল ঘোষণা",
        categories: {
          warning: "সতর্কতা",
          info: "তথ্য",
          update: "আপডেট",
          video: "ভিডিও"
        },
        backToList: "ঘোষণায় ফিরে যান",
        watchVideo: "ভিডিও দেখুন",
        viewImage: "ছবি দেখুন",
        noVideos: "এখনো কোন ভিডিও নেই"
      },
      sos: {
        title: "SOS / জরুরি",
        sendSOS: "SOS পাঠান",
        holdToSend: "SOS পাঠাতে 3 সেকেন্ড ধরে রাখুন",
        confirmTitle: "SOS সতর্কতা নিশ্চিত করুন",
        confirmMessage: "আপনি কি নিশ্চিত যে আপনি জরুরি SOS সতর্কতা পাঠাতে চান? এটি অবিলম্বে MyShield প্রশাসকদের আপনার জরুরি অবস্থা সম্পর্কে জানাবে।",
        confirmYes: "হ্যাঁ, SOS পাঠান",
        confirmNo: "বাতিল",
        successTitle: "SOS সতর্কতা পাঠানো হয়েছে",
        successMessage: "আপনার জরুরি সতর্কতা পাঠানো হয়েছে। আমাদের দল যত তাড়াতাড়ি সম্ভব আপনার সাথে যোগাযোগ করবে।",
        emergencyContacts: "জরুরি যোগাযোগ",
        myshieldHotline: "MyShield হটলাইন",
        labourDept: "শ্রম বিভাগ (JTKSM)",
        police: "পুলিশ",
        ambulance: "অ্যাম্বুলেন্স",
        sosHistory: "SOS ইতিহাস",
        noHistory: "কোন পূর্ববর্তী SOS সতর্কতা নেই",
        status: {
          pending: "বিচারাধীন",
          in_progress: "প্রক্রিয়াধীন",
          resolved: "সমাধান হয়েছে"
        },
        stayCalm: "শান্ত ও নিরাপদ থাকুন। সাহায্য আসছে।",
        callDirectly: "তাৎক্ষণিক সহায়তার জন্য সরাসরি কল করুন"
      },
      vacancies: {
        title: "চাকরির শূন্যপদ",
        searchPlaceholder: "চাকরি খুঁজুন...",
        filters: "ফিল্টার",
        allIndustries: "সব শিল্প",
        allLocations: "সব অবস্থান",
        allTypes: "সব ধরন",
        salaryRange: "বেতন সীমা",
        clearFilters: "ফিল্টার মুছুন",
        jobType: {
          full_time: "পূর্ণ সময়",
          part_time: "খণ্ডকালীন",
          contract: "চুক্তি",
          temporary: "অস্থায়ী"
        },
        perMonth: "প্রতি মাসে",
        postedOn: "পোস্ট করা হয়েছে",
        deadline: "শেষ তারিখ",
        apply: "আবেদন",
        applied: "আবেদন করা হয়েছে",
        viewDetails: "বিস্তারিত দেখুন",
        requirements: "প্রয়োজনীয়তা",
        description: "বিবরণ",
        applyNow: "এখনই আবেদন করুন",
        applicationSent: "আবেদন পাঠানো হয়েছে!",
        alreadyApplied: "আপনি ইতিমধ্যে আবেদন করেছেন",
        backToJobs: "চাকরিতে ফিরে যান",
        myApplications: "আমার আবেদন",
        noJobs: "কোন চাকরির শূন্যপদ নেই",
        noApplications: "আপনি এখনো কোন চাকরিতে আবেদন করেননি",
        coverLetter: "কভার লেটার (ঐচ্ছিক)",
        coverLetterPlaceholder: "নিজের সম্পর্কে সংক্ষেপে লিখুন...",
        confirmApply: "আবেদন নিশ্চিত করুন",
        confirmApplyMessage: "আপনি কি এই পদের জন্য আবেদন করতে চান?",
        applicationStatus: {
          submitted: "জমা দেওয়া হয়েছে",
          reviewing: "পর্যালোচনাধীন",
          interview: "সাক্ষাৎকার",
          accepted: "গৃহীত",
          rejected: "নির্বাচিত হয়নি"
        },
        companyName: "কোম্পানি",
        location: "অবস্থান",
        salary: "বেতন",
        applicants: "আবেদনকারী"
      }
    },

    staff: {
      title: "MyShield স্টাফ",
      subtitle: "শ্রমিক ব্যবস্থাপনা",
      nav: {
        myCases: "আমার কেস",
        messages: "বার্তা"
      },
      cases: {
        title: "নির্ধারিত কেস",
        worker: "শ্রমিক",
        assignedDate: "নির্ধারিত তারিখ",
        noCases: "কোন কেস নির্ধারিত নেই"
      }
    },

    admin: {
      title: "অ্যাডমিন পোর্টাল",
      nav: {
        dashboard: "ড্যাশবোর্ড",
        visaTracker: "ভিসা ট্র্যাকার",
        chatOversight: "চ্যাট তত্ত্বাবধান",
        members: "সদস্য",
        staffManagement: "স্টাফ ব্যবস্থাপনা",
        reports: "রিপোর্ট",
        analytics: "বিশ্লেষণ",
        announcements: "ঘোষণা"
      },
      dashboard: {
        welcome: "ফিরে আসার জন্য স্বাগতম",
        totalWorkers: "মোট শ্রমিক",
        activeComplaints: "সক্রিয় অভিযোগ",
        totalStaff: "মোট স্টাফ",
        monthlyRevenue: "মাসিক আয়"
      },
      members: {
        title: "সকল সদস্য",
        addMember: "সদস্য যোগ করুন",
        viewProfile: "প্রোফাইল দেখুন"
      },
      visaTracker: {
        title: "ভিসা ও পারমিট ট্র্যাকার",
        expiringSoon: "শীঘ্রই মেয়াদ শেষ",
        expired: "মেয়াদ শেষ",
        valid: "বৈধ",
        sendAlerts: "সতর্কতা পাঠান"
      },
      reports: {
        title: "রিপোর্ট",
        generate: "রিপোর্ট তৈরি",
        download: "ডাউনলোড",
        workers: "শ্রমিক রিপোর্ট",
        complaints: "অভিযোগ রিপোর্ট",
        payments: "পেমেন্ট রিপোর্ট"
      },
      announcements: {
        title: "ঘোষণা ব্যবস্থাপনা",
        createNew: "ঘোষণা তৈরি করুন",
        editAnnouncement: "ঘোষণা সম্পাদনা",
        deleteAnnouncement: "ঘোষণা মুছুন",
        confirmDelete: "আপনি কি এই ঘোষণা মুছতে চান?",
        titleLabel: "শিরোনাম",
        bodyLabel: "বিষয়বস্তু",
        categoryLabel: "বিভাগ",
        imageLabel: "ছবি (ঐচ্ছিক)",
        videoLabel: "ভিডিও URL (YouTube বা আপলোড)",
        expiryLabel: "মেয়াদ শেষের তারিখ (ঐচ্ছিক)",
        pinAnnouncement: "ঘোষণা পিন করুন",
        unpinAnnouncement: "আনপিন",
        publish: "প্রকাশ করুন",
        unpublish: "অপ্রকাশিত করুন",
        published: "প্রকাশিত",
        draft: "খসড়া",
        expired: "মেয়াদ শেষ",
        readCount: "দেখা হয়েছে",
        uploadMedia: "মিডিয়া আপলোড",
        uploading: "আপলোড হচ্ছে...",
        youtubeUrl: "YouTube URL",
        pasteYoutubeUrl: "YouTube ভিডিও URL পেস্ট করুন",
        orUploadVideo: "বা ভিডিও ফাইল আপলোড করুন",
        categories: {
          warning: "সতর্কতা",
          info: "তথ্য",
          update: "আপডেট",
          video: "ভিডিও"
        },
        noAnnouncements: "এখনো কোন ঘোষণা তৈরি হয়নি",
        createFirst: "শ্রমিকদের জানাতে আপনার প্রথম ঘোষণা তৈরি করুন"
      },
      jobs: {
        title: "চাকরি ব্যবস্থাপনা",
        subtitle: "শ্রমিকদের জন্য চাকরির পোস্টিং তৈরি এবং পরিচালনা করুন",
        createJob: "চাকরির পোস্টিং তৈরি করুন",
        editJob: "চাকরির পোস্টিং সম্পাদনা করুন",
        deleteJob: "চাকরি মুছুন",
        confirmDelete: "আপনি কি এই চাকরি মুছতে চান? সমস্ত আবেদনও মুছে যাবে।",
        jobTitle: "চাকরির শিরোনাম",
        companyName: "কোম্পানির নাম",
        location: "অবস্থান",
        salaryMin: "সর্বনিম্ন বেতন (RM)",
        salaryMax: "সর্বোচ্চ বেতন (RM)",
        jobType: "চাকরির ধরন",
        industry: "শিল্প",
        description: "চাকরির বিবরণ",
        requirements: "প্রয়োজনীয়তা",
        applicationDeadline: "আবেদনের শেষ তারিখ",
        published: "প্রকাশিত",
        unpublished: "অপ্রকাশিত",
        expired: "মেয়াদ শেষ",
        publish: "প্রকাশ করুন",
        unpublish: "অপ্রকাশিত করুন",
        noJobs: "এখনো কোন চাকরির পোস্টিং নেই",
        createFirst: "শ্রমিকদের আবেদনের জন্য আপনার প্রথম চাকরির পোস্টিং তৈরি করুন",
        applicants: "আবেদনকারী",
        viewApplicants: "আবেদনকারী দেখুন",
        noApplicants: "এই পদের জন্য এখনো কোন আবেদনকারী নেই",
        allApplications: "সমস্ত আবেদন",
        applicantName: "আবেদনকারী",
        jobPosition: "চাকরির পদ",
        appliedOn: "আবেদনের তারিখ",
        coverLetter: "কভার লেটার",
        updateStatus: "স্থিতি আপডেট করুন",
        statusUpdated: "আবেদনের স্থিতি আপডেট হয়েছে!",
        jobTypes: {
          full_time: "পূর্ণকালীন",
          part_time: "খণ্ডকালীন",
          contract: "চুক্তি",
          temporary: "অস্থায়ী"
        },
        industries: {
          general: "সাধারণ",
          construction: "নির্মাণ",
          manufacturing: "উৎপাদন",
          agriculture: "কৃষি",
          hospitality: "আতিথেয়তা",
          services: "সেবা",
          logistics: "লজিস্টিকস",
          retail: "খুচরা"
        },
        applicationStatus: {
          submitted: "জমা দেওয়া হয়েছে",
          reviewing: "পর্যালোচনা চলছে",
          interview: "সাক্ষাৎকার",
          accepted: "গৃহীত",
          rejected: "প্রত্যাখ্যাত"
        }
      }
    }
  },

  ne: {
    logout: "लग आउट",
    signOut: "साइन आउट",
    loading: "लोड हुँदैछ...",
    save: "सेभ गर्नुहोस्",
    cancel: "रद्द गर्नुहोस्",
    submit: "पेश गर्नुहोस्",
    delete: "मेटाउनुहोस्",
    edit: "सम्पादन",
    view: "हेर्नुहोस्",
    search: "खोज्नुहोस्",
    filter: "फिल्टर",
    status: "स्थिति",
    actions: "कार्यहरू",
    noData: "कुनै डाटा छैन",

    member: {
      title: "MyShield",
      subtitle: "कामदार सुरक्षा प्लेटफर्म",
      nav: {
        profile: "प्रोफाइल",
        complaints: "गुनासोहरू",
        payments: "भुक्तानी",
        documents: "कागजातहरू",
        messages: "सन्देशहरू",
        settings: "सेटिङहरू",
        announcements: "घोषणाहरू",
        sos: "SOS / आपतकालीन",
        vacancies: "रोजगारी"
      },
      profile: {
        title: "मेरो प्रोफाइल",
        personalInfo: "व्यक्तिगत जानकारी",
        fullName: "पूरा नाम",
        email: "इमेल",
        phone: "फोन",
        passportNumber: "राहदानी नम्बर",
        nationality: "राष्ट्रियता",
        employer: "रोजगारदाता",
        visaExpiry: "भिसा समाप्ति",
        subscriptionStatus: "सदस्यता स्थिति",
        updateProfile: "प्रोफाइल अपडेट"
      },
      complaints: {
        title: "मेरा गुनासोहरू",
        newComplaint: "नयाँ गुनासो",
        complaintTitle: "शीर्षक",
        description: "विवरण",
        category: "वर्ग",
        priority: "प्राथमिकता",
        submitComplaint: "गुनासो पेश गर्नुहोस्",
        noComplaints: "अझै कुनै गुनासो छैन"
      },
      payments: {
        title: "भुक्तानी इतिहास",
        makePayment: "भुक्तानी गर्नुहोस्",
        amount: "रकम",
        date: "मिति",
        method: "विधि",
        qrPayment: "QR कोड भुक्तानी"
      },
      documents: {
        title: "मेरा कागजातहरू",
        uploadDocument: "कागजात अपलोड",
        documentType: "कागजात प्रकार",
        passport: "राहदानी",
        visa: "भिसा/कार्य अनुमति",
        contract: "रोजगार सम्झौता",
        other: "अन्य"
      },
      chat: {
        title: "सन्देशहरू",
        sendMessage: "सन्देश पठाउनुहोस्...",
        online: "अनलाइन",
        offline: "अफलाइन",
        lastSeen: "अन्तिम पटक देखिएको"
      },
      settings: {
        title: "सेटिङहरू",
        language: "भाषा",
        languageDesc: "ड्यासबोर्डको लागि आफ्नो रुचाइएको भाषा छान्नुहोस्",
        accountInfo: "खाता जानकारी",
        email: "इमेल",
        memberId: "सदस्य आईडी",
        accountStatus: "खाता स्थिति"
      },
      announcements: {
        title: "घोषणाहरू",
        tabLabel: "घोषणाहरू",
        noAnnouncements: "कुनै घोषणाहरू छैनन्",
        readMore: "थप पढ्नुहोस्",
        shareWhatsApp: "WhatsApp मा साझा गर्नुहोस्",
        pinned: "पिन गरिएको",
        postedOn: "पोस्ट गरिएको",
        videoAwareness: "जागरूकता भिडियोहरू",
        videoAwarenessDesc: "कामदार अधिकार र सुरक्षाबारे महत्त्वपूर्ण भिडियोहरू",
        workerRights: "कामदार अधिकार",
        tutorials: "ट्युटोरियलहरू",
        safetyVideos: "सुरक्षा भिडियोहरू",
        legalAid: "कानुनी सहायता",
        allAnnouncements: "सबै घोषणाहरू",
        categories: {
          warning: "चेतावनी",
          info: "जानकारी",
          update: "अपडेट",
          video: "भिडियो"
        },
        backToList: "घोषणाहरूमा फर्कनुहोस्",
        watchVideo: "भिडियो हेर्नुहोस्",
        viewImage: "तस्वीर हेर्नुहोस्",
        noVideos: "अझै कुनै भिडियो छैन"
      },
      sos: {
        title: "SOS / आपतकालीन",
        sendSOS: "SOS पठाउनुहोस्",
        holdToSend: "SOS पठाउन 3 सेकेन्ड थिच्नुहोस्",
        confirmTitle: "SOS अलर्ट पुष्टि गर्नुहोस्",
        confirmMessage: "के तपाईं निश्चित हुनुहुन्छ कि तपाईं आपतकालीन SOS अलर्ट पठाउन चाहनुहुन्छ? यसले तुरुन्तै MyShield प्रशासकहरूलाई तपाईंको आपतकालीन अवस्थाको बारेमा सूचित गर्नेछ।",
        confirmYes: "हो, SOS पठाउनुहोस्",
        confirmNo: "रद्द गर्नुहोस्",
        successTitle: "SOS अलर्ट पठाइयो",
        successMessage: "तपाईंको आपतकालीन अलर्ट पठाइएको छ। हाम्रो टोलीले सकेसम्म चाँडो तपाईंलाई सम्पर्क गर्नेछ।",
        emergencyContacts: "आपतकालीन सम्पर्कहरू",
        myshieldHotline: "MyShield हटलाइन",
        labourDept: "श्रम विभाग (JTKSM)",
        police: "प्रहरी",
        ambulance: "एम्बुलेन्स",
        sosHistory: "SOS इतिहास",
        noHistory: "कुनै पहिलेको SOS अलर्टहरू छैनन्",
        status: {
          pending: "लम्बित",
          in_progress: "प्रक्रियामा",
          resolved: "समाधान भयो"
        },
        stayCalm: "शान्त र सुरक्षित रहनुहोस्। मद्दत आउँदैछ।",
        callDirectly: "तुरुन्त सहायताको लागि सिधै कल गर्नुहोस्"
      },
      vacancies: {
        title: "जागिर रिक्तता",
        searchPlaceholder: "जागिर खोज्नुहोस्...",
        filters: "फिल्टरहरू",
        allIndustries: "सबै उद्योग",
        allLocations: "सबै स्थान",
        allTypes: "सबै प्रकार",
        salaryRange: "तलब सीमा",
        clearFilters: "फिल्टर हटाउनुहोस्",
        jobType: {
          full_time: "पूर्ण समय",
          part_time: "आंशिक समय",
          contract: "करार",
          temporary: "अस्थायी"
        },
        perMonth: "प्रति महिना",
        postedOn: "पोस्ट गरिएको",
        deadline: "अन्तिम मिति",
        apply: "आवेदन",
        applied: "आवेदन गरियो",
        viewDetails: "विवरण हेर्नुहोस्",
        requirements: "आवश्यकताहरू",
        description: "विवरण",
        applyNow: "अहिले आवेदन गर्नुहोस्",
        applicationSent: "आवेदन पठाइयो!",
        alreadyApplied: "तपाईंले पहिले नै आवेदन गर्नुभएको छ",
        backToJobs: "जागिरमा फर्कनुहोस्",
        myApplications: "मेरा आवेदनहरू",
        noJobs: "कुनै जागिर रिक्तता छैन",
        noApplications: "तपाईंले अझै कुनै जागिरमा आवेदन गर्नुभएको छैन",
        coverLetter: "कभर लेटर (वैकल्पिक)",
        coverLetterPlaceholder: "आफ्नो बारेमा संक्षिप्त परिचय लेख्नुहोस्...",
        confirmApply: "आवेदन पुष्टि गर्नुहोस्",
        confirmApplyMessage: "के तपाईं यस पदको लागि आवेदन गर्न चाहनुहुन्छ?",
        applicationStatus: {
          submitted: "पेश गरियो",
          reviewing: "समीक्षामा",
          interview: "अन्तर्वार्ता",
          accepted: "स्वीकृत",
          rejected: "छनोट भएन"
        },
        companyName: "कम्पनी",
        location: "स्थान",
        salary: "तलब",
        applicants: "आवेदकहरू"
      }
    },

    staff: {
      title: "MyShield कर्मचारी",
      subtitle: "कामदार व्यवस्थापन",
      nav: {
        myCases: "मेरा केसहरू",
        messages: "सन्देशहरू"
      },
      cases: {
        title: "तोकिएका केसहरू",
        worker: "कामदार",
        assignedDate: "तोकिएको मिति",
        noCases: "कुनै केस तोकिएको छैन"
      }
    },

    admin: {
      title: "एडमिन पोर्टल",
      nav: {
        dashboard: "ड्यासबोर्ड",
        visaTracker: "भिसा ट्र्याकर",
        chatOversight: "च्याट निगरानी",
        members: "सदस्यहरू",
        staffManagement: "कर्मचारी व्यवस्थापन",
        reports: "रिपोर्टहरू",
        analytics: "विश्लेषण",
        announcements: "घोषणाहरू"
      },
      dashboard: {
        welcome: "फेरि स्वागत छ",
        totalWorkers: "कुल कामदारहरू",
        activeComplaints: "सक्रिय गुनासोहरू",
        totalStaff: "कुल कर्मचारी",
        monthlyRevenue: "मासिक आम्दानी"
      },
      members: {
        title: "सबै सदस्यहरू",
        addMember: "सदस्य थप्नुहोस्",
        viewProfile: "प्रोफाइल हेर्नुहोस्"
      },
      visaTracker: {
        title: "भिसा र अनुमति ट्र्याकर",
        expiringSoon: "चाँडै समाप्त",
        expired: "समाप्त भयो",
        valid: "मान्य",
        sendAlerts: "अलर्ट पठाउनुहोस्"
      },
      reports: {
        title: "रिपोर्टहरू",
        generate: "रिपोर्ट बनाउनुहोस्",
        download: "डाउनलोड",
        workers: "कामदार रिपोर्ट",
        complaints: "गुनासो रिपोर्ट",
        payments: "भुक्तानी रिपोर्ट"
      },
      announcements: {
        title: "घोषणा व्यवस्थापन",
        createNew: "घोषणा बनाउनुहोस्",
        editAnnouncement: "घोषणा सम्पादन",
        deleteAnnouncement: "घोषणा मेटाउनुहोस्",
        confirmDelete: "के तपाईं यो घोषणा मेटाउन चाहनुहुन्छ?",
        titleLabel: "शीर्षक",
        bodyLabel: "सामग्री",
        categoryLabel: "वर्ग",
        imageLabel: "तस्वीर (वैकल्पिक)",
        videoLabel: "भिडियो URL (YouTube वा अपलोड)",
        expiryLabel: "समाप्ति मिति (वैकल्पिक)",
        pinAnnouncement: "घोषणा पिन गर्नुहोस्",
        unpinAnnouncement: "अनपिन",
        publish: "प्रकाशित गर्नुहोस्",
        unpublish: "अप्रकाशित",
        published: "प्रकाशित",
        draft: "ड्राफ्ट",
        expired: "समाप्त",
        readCount: "हेराइहरू",
        uploadMedia: "मिडिया अपलोड",
        uploading: "अपलोड हुँदैछ...",
        youtubeUrl: "YouTube URL",
        pasteYoutubeUrl: "YouTube भिडियो URL टाँस्नुहोस्",
        orUploadVideo: "वा भिडियो फाइल अपलोड गर्नुहोस्",
        categories: {
          warning: "चेतावनी",
          info: "जानकारी",
          update: "अपडेट",
          video: "भिडियो"
        },
        noAnnouncements: "अझै कुनै घोषणाहरू बनाइएको छैन",
        createFirst: "कामदारहरूलाई जानकारी दिन आफ्नो पहिलो घोषणा बनाउनुहोस्"
      },
      jobs: {
        title: "रोजगार व्यवस्थापन",
        subtitle: "कामदारहरूको लागि जागिर पोस्टिङ बनाउनुहोस् र व्यवस्थापन गर्नुहोस्",
        createJob: "जागिर पोस्टिङ बनाउनुहोस्",
        editJob: "जागिर पोस्टिङ सम्पादन",
        deleteJob: "जागिर मेटाउनुहोस्",
        confirmDelete: "के तपाईं यो जागिर मेटाउन चाहनुहुन्छ? सबै आवेदनहरू पनि मेटिनेछन्।",
        jobTitle: "जागिर शीर्षक",
        companyName: "कम्पनीको नाम",
        location: "स्थान",
        salaryMin: "न्यूनतम तलब (RM)",
        salaryMax: "अधिकतम तलब (RM)",
        jobType: "जागिर प्रकार",
        industry: "उद्योग",
        description: "जागिर विवरण",
        requirements: "आवश्यकताहरू",
        applicationDeadline: "आवेदन अन्तिम मिति",
        published: "प्रकाशित",
        unpublished: "अप्रकाशित",
        expired: "समाप्त",
        publish: "प्रकाशित गर्नुहोस्",
        unpublish: "अप्रकाशित",
        noJobs: "अझै कुनै जागिर पोस्टिङ छैन",
        createFirst: "कामदारहरूको आवेदनको लागि आफ्नो पहिलो जागिर पोस्टिङ बनाउनुहोस्",
        applicants: "आवेदकहरू",
        viewApplicants: "आवेदकहरू हेर्नुहोस्",
        noApplicants: "यस पदको लागि अझै कुनै आवेदकहरू छैनन्",
        allApplications: "सबै आवेदनहरू",
        applicantName: "आवेदक",
        jobPosition: "जागिर पद",
        appliedOn: "आवेदन मिति",
        coverLetter: "कभर लेटर",
        updateStatus: "स्थिति अपडेट",
        statusUpdated: "आवेदन स्थिति अपडेट भयो!",
        jobTypes: {
          full_time: "पूर्ण समय",
          part_time: "आंशिक समय",
          contract: "करार",
          temporary: "अस्थायी"
        },
        industries: {
          general: "सामान्य",
          construction: "निर्माण",
          manufacturing: "उत्पादन",
          agriculture: "कृषि",
          hospitality: "आतिथ्य",
          services: "सेवाहरू",
          logistics: "लजिस्टिक्स",
          retail: "खुद्रा"
        },
        applicationStatus: {
          submitted: "पेश गरियो",
          reviewing: "समीक्षामा",
          interview: "अन्तर्वार्ता",
          accepted: "स्वीकृत",
          rejected: "अस्वीकृत"
        }
      }
    }
  },

  fil: {
    logout: "Mag-logout",
    signOut: "Mag-sign Out",
    loading: "Naglo-load...",
    save: "I-save",
    cancel: "Kanselahin",
    submit: "Isumite",
    delete: "Burahin",
    edit: "I-edit",
    view: "Tingnan",
    search: "Maghanap",
    filter: "I-filter",
    status: "Katayuan",
    actions: "Mga Aksyon",
    noData: "Walang data",

    member: {
      title: "MyShield",
      subtitle: "Platform ng Proteksyon ng Manggagawa",
      nav: {
        profile: "Profile",
        complaints: "Mga Reklamo",
        payments: "Mga Bayad",
        documents: "Mga Dokumento",
        messages: "Mga Mensahe"
      },
      profile: {
        title: "Aking Profile",
        personalInfo: "Personal na Impormasyon",
        fullName: "Buong Pangalan",
        email: "Email",
        phone: "Telepono",
        passportNumber: "Numero ng Pasaporte",
        nationality: "Nasyonalidad",
        employer: "Employer",
        visaExpiry: "Expiry ng Visa",
        subscriptionStatus: "Status ng Membership",
        updateProfile: "I-update ang Profile"
      },
      complaints: {
        title: "Mga Reklamo Ko",
        newComplaint: "Bagong Reklamo",
        complaintTitle: "Pamagat",
        description: "Deskripsyon",
        category: "Kategorya",
        priority: "Priyoridad",
        submitComplaint: "Isumite ang Reklamo",
        noComplaints: "Wala pang mga reklamo"
      },
      payments: {
        title: "Kasaysayan ng Bayad",
        makePayment: "Magbayad",
        amount: "Halaga",
        date: "Petsa",
        method: "Paraan",
        qrPayment: "QR Code na Bayad"
      },
      documents: {
        title: "Mga Dokumento Ko",
        uploadDocument: "Mag-upload ng Dokumento",
        documentType: "Uri ng Dokumento",
        passport: "Pasaporte",
        visa: "Visa/Work Permit",
        contract: "Kontrata sa Trabaho",
        other: "Iba pa"
      },
      chat: {
        title: "Mga Mensahe",
        sendMessage: "Magpadala ng mensahe...",
        online: "Online",
        offline: "Offline",
        lastSeen: "Huling nakita"
      }
    },

    staff: {
      title: "MyShield Staff",
      subtitle: "Pamamahala ng Manggagawa",
      nav: {
        myCases: "Mga Kaso Ko",
        messages: "Mga Mensahe"
      },
      cases: {
        title: "Mga Itinalagang Kaso",
        worker: "Manggagawa",
        assignedDate: "Petsa ng Pagtatalaga",
        noCases: "Walang itinalagang kaso"
      }
    },

    admin: {
      title: "Admin Portal",
      nav: {
        dashboard: "Dashboard",
        visaTracker: "Visa Tracker",
        chatOversight: "Chat Oversight",
        members: "Mga Miyembro",
        staffManagement: "Pamamahala ng Staff",
        reports: "Mga Ulat",
        analytics: "Analytics"
      },
      dashboard: {
        welcome: "Maligayang pagbabalik",
        totalWorkers: "Kabuuang Manggagawa",
        activeComplaints: "Mga Aktibong Reklamo",
        totalStaff: "Kabuuang Staff",
        monthlyRevenue: "Buwanang Kita"
      },
      members: {
        title: "Lahat ng Miyembro",
        addMember: "Magdagdag ng Miyembro",
        viewProfile: "Tingnan ang Profile"
      },
      visaTracker: {
        title: "Visa at Permit Tracker",
        expiringSoon: "Malapit nang Mag-expire",
        expired: "Nag-expire na",
        valid: "Valid",
        sendAlerts: "Magpadala ng Alerto"
      },
      reports: {
        title: "Mga Ulat",
        generate: "Gumawa ng Ulat",
        download: "I-download",
        workers: "Ulat ng Manggagawa",
        complaints: "Ulat ng Reklamo",
        payments: "Ulat ng Bayad"
      }
    }
  },

  my: {
    logout: "ထွက်မည်",
    signOut: "ထွက်မည်",
    loading: "ဖွင့်နေသည်...",
    save: "သိမ်းမည်",
    cancel: "ပယ်ဖျက်",
    submit: "တင်သွင်းမည်",
    delete: "ဖျက်မည်",
    edit: "ပြင်ဆင်",
    view: "ကြည့်ရန်",
    search: "ရှာဖွေ",
    filter: "စစ်ထုတ်",
    status: "အခြေအနေ",
    actions: "လုပ်ဆောင်ချက်များ",
    noData: "ဒေတာမရှိပါ",

    member: {
      title: "MyShield",
      subtitle: "အလုပ်သမားကာကွယ်ရေးစနစ်",
      nav: {
        profile: "ပရိုဖိုင်",
        complaints: "တိုင်ကြားချက်များ",
        payments: "ငွေပေးချေမှုများ",
        documents: "စာရွက်စာတမ်းများ",
        messages: "မက်ဆေ့ချ်များ",
        settings: "ဆက်တင်များ",
        announcements: "ကြေညာချက်များ",
        sos: "SOS / အရေးပေါ်",
        vacancies: "အလုပ်အကိုင်များ"
      },
      profile: {
        title: "ကျွန်ုပ်၏ပရိုဖိုင်",
        personalInfo: "ကိုယ်ရေးအချက်အလက်",
        fullName: "အမည်အပြည့်အစုံ",
        email: "အီးမေးလ်",
        phone: "ဖုန်း",
        passportNumber: "နိုင်ငံကူးလက်မှတ်နံပါတ်",
        nationality: "နိုင်ငံသား",
        employer: "အလုပ်ရှင်",
        visaExpiry: "ဗီဇာသက်တမ်း",
        subscriptionStatus: "အသင်းဝင်အခြေအနေ",
        updateProfile: "ပရိုဖိုင်အပ်ဒိတ်"
      },
      complaints: {
        title: "ကျွန်ုပ်၏တိုင်ကြားချက်များ",
        newComplaint: "တိုင်ကြားချက်အသစ်",
        complaintTitle: "ခေါင်းစဉ်",
        description: "အသေးစိတ်",
        category: "အမျိုးအစား",
        priority: "ဦးစားပေး",
        submitComplaint: "တိုင်ကြားချက်တင်သွင်းရန်",
        noComplaints: "တိုင်ကြားချက်မရှိသေးပါ"
      },
      payments: {
        title: "ငွေပေးချေမှုမှတ်တမ်း",
        makePayment: "ငွေပေးချေရန်",
        amount: "ပမာဏ",
        date: "ရက်စွဲ",
        method: "နည်းလမ်း",
        qrPayment: "QR ကုဒ်ငွေပေးချေမှု"
      },
      documents: {
        title: "ကျွန်ုပ်၏စာရွက်စာတမ်းများ",
        uploadDocument: "စာရွက်စာတမ်းတင်ရန်",
        documentType: "စာရွက်စာတမ်းအမျိုးအစား",
        passport: "နိုင်ငံကူးလက်မှတ်",
        visa: "ဗီဇာ/အလုပ်ခွင့်ပြုလက်မှတ်",
        contract: "အလုပ်စာချုပ်",
        other: "အခြား"
      },
      chat: {
        title: "မက်ဆေ့ချ်များ",
        sendMessage: "မက်ဆေ့ချ်ပို့ရန်...",
        online: "အွန်လိုင်း",
        offline: "အော့ဖ်လိုင်း",
        lastSeen: "နောက်ဆုံးမြင်တွေ့ခဲ့သည်"
      },
      settings: {
        title: "ဆက်တင်များ",
        language: "ဘာသာစကား",
        languageDesc: "ဒက်ရှ်ဘုတ်အတွက် သင်နှစ်သက်ရာဘာသာစကားကိုရွေးချယ်ပါ",
        accountInfo: "အကောင့်အချက်အလက်",
        email: "အီးမေးလ်",
        memberId: "အဖွဲ့ဝင် ID",
        accountStatus: "အကောင့်အခြေအနေ"
      },
      announcements: {
        title: "ကြေညာချက်များ",
        tabLabel: "ကြေညာချက်များ",
        noAnnouncements: "ကြေညာချက်မရှိပါ",
        readMore: "ပိုမိုဖတ်ရန်",
        shareWhatsApp: "WhatsApp တွင်မျှဝေရန်",
        pinned: "ပင်ထိုးထားသည်",
        postedOn: "တင်ထားသည့်ရက်",
        videoAwareness: "သိရှိနားလည်မှုဗီဒီယိုများ",
        videoAwarenessDesc: "အလုပ်သမားအခွင့်အရေးနှင့် ဘေးကင်းရေးဆိုင်ရာ အရေးကြီးသော ဗီဒီယိုများ",
        workerRights: "အလုပ်သမားအခွင့်အရေး",
        tutorials: "သင်ခန်းစာများ",
        safetyVideos: "ဘေးကင်းရေးဗီဒီယိုများ",
        legalAid: "ဥပဒေအကူအညီ",
        allAnnouncements: "ကြေညာချက်အားလုံး",
        categories: {
          warning: "သတိပေးချက်",
          info: "အချက်အလက်",
          update: "အပ်ဒိတ်",
          video: "ဗီဒီယို"
        },
        backToList: "ကြေညာချက်များသို့ပြန်သွားရန်",
        watchVideo: "ဗီဒီယိုကြည့်ရန်",
        viewImage: "ပုံကြည့်ရန်",
        noVideos: "ဗီဒီယိုမရှိသေးပါ"
      },
      sos: {
        title: "SOS / အရေးပေါ်",
        sendSOS: "SOS ပို့ရန်",
        holdToSend: "SOS ပို့ရန် 3 စက္ကန့် ဖိထားပါ",
        confirmTitle: "SOS သတိပေးချက်အတည်ပြုရန်",
        confirmMessage: "အရေးပေါ် SOS သတိပေးချက်ပို့လိုသည်မှာ သေချာပါသလား။ ၎င်းသည် MyShield စီမံခန့်ခွဲသူများကို သင်၏အရေးပေါ်အခြေအနေကို ချက်ချင်းအသိပေးပါမည်။",
        confirmYes: "ဟုတ်ကဲ့၊ SOS ပို့ပါ",
        confirmNo: "မလုပ်တော့ပါ",
        successTitle: "SOS သတိပေးချက်ပို့ပြီးပါပြီ",
        successMessage: "သင်၏အရေးပေါ်သတိပေးချက်ကို ပို့ပြီးပါပြီ။ ကျွန်ုပ်တို့အဖွဲ့မှ အမြန်ဆုံးဆက်သွယ်ပါမည်။",
        emergencyContacts: "အရေးပေါ်ဆက်သွယ်ရန်",
        myshieldHotline: "MyShield ဟော့လိုင်း",
        labourDept: "အလုပ်သမားဌာန (JTKSM)",
        police: "ရဲ",
        ambulance: "လူနာတင်ယာဉ်",
        sosHistory: "SOS မှတ်တမ်း",
        noHistory: "ယခင် SOS သတိပေးချက်များမရှိပါ",
        status: {
          pending: "စောင့်ဆိုင်းဆဲ",
          in_progress: "လုပ်ဆောင်နေဆဲ",
          resolved: "ဖြေရှင်းပြီး"
        },
        stayCalm: "တည်ငြိမ်စွာနှင့် ဘေးကင်းစွာနေပါ။ အကူအညီလာနေပါပြီ။",
        callDirectly: "ချက်ချင်းအကူအညီအတွက် တိုက်ရိုက်ဖုန်းခေါ်ပါ"
      },
      vacancies: {
        title: "အလုပ်လစ်လပ်နေရာများ",
        searchPlaceholder: "အလုပ်ရှာဖွေရန်...",
        filters: "စစ်ထုတ်မှုများ",
        allIndustries: "လုပ်ငန်းအားလုံး",
        allLocations: "နေရာအားလုံး",
        allTypes: "အမျိုးအစားအားလုံး",
        salaryRange: "လစာပမာဏ",
        clearFilters: "စစ်ထုတ်မှုများရှင်းလင်းရန်",
        jobType: {
          full_time: "အချိန်ပြည့်",
          part_time: "အချိန်ပိုင်း",
          contract: "စာချုပ်",
          temporary: "ယာယီ"
        },
        perMonth: "တစ်လ",
        postedOn: "တင်ထားသည့်ရက်",
        deadline: "နောက်ဆုံးရက်",
        apply: "လျှောက်ထားရန်",
        applied: "လျှောက်ထားပြီး",
        viewDetails: "အသေးစိတ်ကြည့်ရန်",
        requirements: "လိုအပ်ချက်များ",
        description: "ဖော်ပြချက်",
        applyNow: "ယခုလျှောက်ထားရန်",
        applicationSent: "လျှောက်လွှာပို့ပြီးပါပြီ!",
        alreadyApplied: "သင်လျှောက်ထားပြီးပါပြီ",
        backToJobs: "အလုပ်များသို့ပြန်သွားရန်",
        myApplications: "ကျွန်ုပ်၏လျှောက်လွှာများ",
        noJobs: "အလုပ်လစ်လပ်နေရာမရှိပါ",
        noApplications: "သင်မည်သည့်အလုပ်မှမလျှောက်ရသေးပါ",
        coverLetter: "မိတ်ဆက်စာ (ရွေးချယ်နိုင်)",
        coverLetterPlaceholder: "သင့်ကိုယ်သင်အကျဉ်းချုပ်မိတ်ဆက်ပါ...",
        confirmApply: "လျှောက်လွှာအတည်ပြုရန်",
        confirmApplyMessage: "ဤရာထူးအတွက်လျှောက်ထားလိုသည်မှာ သေချာပါသလား?",
        applicationStatus: {
          submitted: "တင်သွင်းပြီး",
          reviewing: "စိစစ်နေဆဲ",
          interview: "အင်တာဗျူး",
          accepted: "လက်ခံပြီး",
          rejected: "မရွေးချယ်"
        },
        companyName: "ကုမ္ပဏီ",
        location: "တည်နေရာ",
        salary: "လစာ",
        applicants: "လျှောက်ထားသူများ"
      }
    },

    staff: {
      title: "MyShield ဝန်ထမ်း",
      subtitle: "အလုပ်သမားစီမံခန့်ခွဲမှု",
      nav: {
        myCases: "ကျွန်ုပ်၏ကိစ္စများ",
        messages: "မက်ဆေ့ချ်များ"
      },
      cases: {
        title: "တာဝန်ပေးထားသောကိစ္စများ",
        worker: "အလုပ်သမား",
        assignedDate: "တာဝန်ပေးသည့်ရက်",
        noCases: "တာဝန်ပေးထားသောကိစ္စမရှိပါ"
      }
    },

    admin: {
      title: "အက်ဒမင်ပေါ်တယ်",
      nav: {
        dashboard: "ဒက်ရှ်ဘုတ်",
        visaTracker: "ဗီဇာခြေရာခံ",
        chatOversight: "ချက်တ်ကြီးကြပ်မှု",
        members: "အဖွဲ့ဝင်များ",
        staffManagement: "ဝန်ထမ်းစီမံခန့်ခွဲမှု",
        reports: "အစီရင်ခံစာများ",
        analytics: "ခွဲခြမ်းစိတ်ဖြာမှု",
        announcements: "ကြေညာချက်များ"
      },
      dashboard: {
        welcome: "ပြန်လာတာကြိုဆိုပါတယ်",
        totalWorkers: "စုစုပေါင်းအလုပ်သမားများ",
        activeComplaints: "လက်ရှိတိုင်ကြားချက်များ",
        totalStaff: "စုစုပေါင်းဝန်ထမ်းများ",
        monthlyRevenue: "လစဉ်ဝင်ငွေ"
      },
      members: {
        title: "အဖွဲ့ဝင်အားလုံး",
        addMember: "အဖွဲ့ဝင်ထည့်ရန်",
        viewProfile: "ပရိုဖိုင်ကြည့်ရန်"
      },
      visaTracker: {
        title: "ဗီဇာနှင့်ခွင့်ပြုလက်မှတ်ခြေရာခံ",
        expiringSoon: "မကြာမီသက်တမ်းကုန်မည်",
        expired: "သက်တမ်းကုန်သွားပြီ",
        valid: "အကျုံးဝင်",
        sendAlerts: "သတိပေးချက်ပို့ရန်"
      },
      reports: {
        title: "အစီရင်ခံစာများ",
        generate: "အစီရင်ခံစာပြုလုပ်ရန်",
        download: "ဒေါင်းလုဒ်",
        workers: "အလုပ်သမားအစီရင်ခံစာ",
        complaints: "တိုင်ကြားချက်အစီရင်ခံစာ",
        payments: "ငွေပေးချေမှုအစီရင်ခံစာ"
      },
      announcements: {
        title: "ကြေညာချက်စီမံခန့်ခွဲမှု",
        createNew: "ကြေညာချက်ဖန်တီးရန်",
        editAnnouncement: "ကြေညာချက်ပြင်ဆင်ရန်",
        deleteAnnouncement: "ကြေညာချက်ဖျက်ရန်",
        confirmDelete: "ဤကြေညာချက်ကို ဖျက်လိုသည်မှာ သေချာပါသလား?",
        titleLabel: "ခေါင်းစဉ်",
        bodyLabel: "အကြောင်းအရာ",
        categoryLabel: "အမျိုးအစား",
        imageLabel: "ပုံ (ရွေးချယ်နိုင်)",
        videoLabel: "ဗီဒီယို URL (YouTube သို့မဟုတ် တင်ရန်)",
        expiryLabel: "သက်တမ်းကုန်ဆုံးရက် (ရွေးချယ်နိုင်)",
        pinAnnouncement: "ကြေညာချက်ပင်ထိုးရန်",
        unpinAnnouncement: "ပင်ဖြုတ်ရန်",
        publish: "ထုတ်ဝေရန်",
        unpublish: "ထုတ်ဝေမှုရုပ်သိမ်းရန်",
        published: "ထုတ်ဝေပြီး",
        draft: "မူကြမ်း",
        expired: "သက်တမ်းကုန်ပြီး",
        readCount: "ကြည့်ရှုမှုများ",
        uploadMedia: "မီဒီယာတင်ရန်",
        uploading: "တင်နေသည်...",
        youtubeUrl: "YouTube URL",
        pasteYoutubeUrl: "YouTube ဗီဒီယို URL ကူးထည့်ပါ",
        orUploadVideo: "သို့မဟုတ် ဗီဒီယိုဖိုင်တင်ပါ",
        categories: {
          warning: "သတိပေးချက်",
          info: "အချက်အလက်",
          update: "အပ်ဒိတ်",
          video: "ဗီဒီယို"
        },
        noAnnouncements: "ကြေညာချက်မရှိသေးပါ",
        createFirst: "အလုပ်သမားများကို အသိပေးရန် ပထမဆုံးကြေညာချက်ဖန်တီးပါ"
      },
      jobs: {
        title: "အလုပ်စီမံခန့်ခွဲမှု",
        subtitle: "အလုပ်သမားများအတွက် အလုပ်ကြေညာချက်များဖန်တီးပြီး စီမံခန့်ခွဲပါ",
        createJob: "အလုပ်ကြေညာချက်ဖန်တီးရန်",
        editJob: "အလုပ်ကြေညာချက်ပြင်ဆင်ရန်",
        deleteJob: "အလုပ်ဖျက်ရန်",
        confirmDelete: "ဤအလုပ်ကို ဖျက်လိုသည်မှာ သေချာပါသလား? လျှောက်လွှာအားလုံးလည်း ဖျက်မည်ဖြစ်သည်။",
        jobTitle: "အလုပ်ခေါင်းစဉ်",
        companyName: "ကုမ္ပဏီအမည်",
        location: "တည်နေရာ",
        salaryMin: "အနိမ့်ဆုံးလစာ (RM)",
        salaryMax: "အမြင့်ဆုံးလစာ (RM)",
        jobType: "အလုပ်အမျိုးအစား",
        industry: "လုပ်ငန်း",
        description: "အလုပ်ဖော်ပြချက်",
        requirements: "လိုအပ်ချက်များ",
        applicationDeadline: "လျှောက်လွှာနောက်ဆုံးရက်",
        published: "ထုတ်ဝေပြီး",
        unpublished: "မထုတ်ဝေရသေး",
        expired: "သက်တမ်းကုန်ပြီး",
        publish: "ထုတ်ဝေရန်",
        unpublish: "ထုတ်ဝေမှုရုပ်သိမ်းရန်",
        noJobs: "အလုပ်ကြေညာချက်မရှိသေးပါ",
        createFirst: "အလုပ်သမားများလျှောက်ထားနိုင်ရန် ပထမဆုံးအလုပ်ကြေညာချက်ဖန်တီးပါ",
        applicants: "လျှောက်ထားသူများ",
        viewApplicants: "လျှောက်ထားသူများကြည့်ရန်",
        noApplicants: "ဤရာထူးအတွက် လျှောက်ထားသူမရှိသေးပါ",
        allApplications: "လျှောက်လွှာအားလုံး",
        applicantName: "လျှောက်ထားသူ",
        jobPosition: "အလုပ်ရာထူး",
        appliedOn: "လျှောက်ထားသည့်ရက်",
        coverLetter: "မိတ်ဆက်စာ",
        updateStatus: "အခြေအနေအပ်ဒိတ်",
        statusUpdated: "လျှောက်လွှာအခြေအနေ အပ်ဒိတ်ပြီးပါပြီ!",
        jobTypes: {
          full_time: "အချိန်ပြည့်",
          part_time: "အချိန်ပိုင်း",
          contract: "စာချုပ်",
          temporary: "ယာယီ"
        },
        industries: {
          general: "အထွေထွေ",
          construction: "ဆောက်လုပ်ရေး",
          manufacturing: "ကုန်ထုတ်လုပ်မှု",
          agriculture: "စိုက်ပျိုးရေး",
          hospitality: "ဧည့်ဝတ်ပြုမှု",
          services: "ဝန်ဆောင်မှုများ",
          logistics: "သယ်ယူပို့ဆောင်ရေး",
          retail: "လက်လီရောင်းဝယ်ရေး"
        },
        applicationStatus: {
          submitted: "တင်သွင်းပြီး",
          reviewing: "စိစစ်နေဆဲ",
          interview: "အင်တာဗျူး",
          accepted: "လက်ခံပြီး",
          rejected: "ပယ်ချပြီး"
        }
      }
    }
  },

  th: {
    logout: "ออกจากระบบ",
    signOut: "ออกจากระบบ",
    loading: "กำลังโหลด...",
    save: "บันทึก",
    cancel: "ยกเลิก",
    submit: "ส่ง",
    delete: "ลบ",
    edit: "แก้ไข",
    view: "ดู",
    search: "ค้นหา",
    filter: "กรอง",
    status: "สถานะ",
    actions: "การดำเนินการ",
    noData: "ไม่มีข้อมูล",

    member: {
      title: "MyShield",
      subtitle: "แพลตฟอร์มคุ้มครองแรงงาน",
      nav: {
        profile: "โปรไฟล์",
        complaints: "การร้องเรียน",
        payments: "การชำระเงิน",
        documents: "เอกสาร",
        messages: "ข้อความ"
      },
      profile: {
        title: "โปรไฟล์ของฉัน",
        personalInfo: "ข้อมูลส่วนตัว",
        fullName: "ชื่อเต็ม",
        email: "อีเมล",
        phone: "โทรศัพท์",
        passportNumber: "หมายเลขพาสปอร์ต",
        nationality: "สัญชาติ",
        employer: "นายจ้าง",
        visaExpiry: "วีซ่าหมดอายุ",
        subscriptionStatus: "สถานะสมาชิก",
        updateProfile: "อัปเดตโปรไฟล์"
      },
      complaints: {
        title: "การร้องเรียนของฉัน",
        newComplaint: "ร้องเรียนใหม่",
        complaintTitle: "หัวข้อ",
        description: "รายละเอียด",
        category: "หมวดหมู่",
        priority: "ความสำคัญ",
        submitComplaint: "ส่งการร้องเรียน",
        noComplaints: "ยังไม่มีการร้องเรียน"
      },
      payments: {
        title: "ประวัติการชำระเงิน",
        makePayment: "ชำระเงิน",
        amount: "จำนวน",
        date: "วันที่",
        method: "วิธี",
        qrPayment: "ชำระผ่าน QR Code"
      },
      documents: {
        title: "เอกสารของฉัน",
        uploadDocument: "อัปโหลดเอกสาร",
        documentType: "ประเภทเอกสาร",
        passport: "พาสปอร์ต",
        visa: "วีซ่า/ใบอนุญาตทำงาน",
        contract: "สัญญาจ้างงาน",
        other: "อื่นๆ"
      },
      chat: {
        title: "ข้อความ",
        sendMessage: "ส่งข้อความ...",
        online: "ออนไลน์",
        offline: "ออฟไลน์",
        lastSeen: "เห็นล่าสุด"
      }
    },

    staff: {
      title: "เจ้าหน้าที่ MyShield",
      subtitle: "การจัดการแรงงาน",
      nav: {
        myCases: "เคสของฉัน",
        messages: "ข้อความ"
      },
      cases: {
        title: "เคสที่ได้รับมอบหมาย",
        worker: "แรงงาน",
        assignedDate: "วันที่มอบหมาย",
        noCases: "ยังไม่มีเคสที่มอบหมาย"
      }
    },

    admin: {
      title: "พอร์ทัลผู้ดูแล",
      nav: {
        dashboard: "แดชบอร์ด",
        visaTracker: "ติดตามวีซ่า",
        chatOversight: "ดูแลแชท",
        members: "สมาชิก",
        staffManagement: "จัดการเจ้าหน้าที่",
        reports: "รายงาน",
        analytics: "การวิเคราะห์"
      },
      dashboard: {
        welcome: "ยินดีต้อนรับกลับมา",
        totalWorkers: "แรงงานทั้งหมด",
        activeComplaints: "การร้องเรียนที่ใช้งานอยู่",
        totalStaff: "เจ้าหน้าที่ทั้งหมด",
        monthlyRevenue: "รายได้รายเดือน"
      },
      members: {
        title: "สมาชิกทั้งหมด",
        addMember: "เพิ่มสมาชิก",
        viewProfile: "ดูโปรไฟล์"
      },
      visaTracker: {
        title: "ติดตามวีซ่าและใบอนุญาต",
        expiringSoon: "ใกล้หมดอายุ",
        expired: "หมดอายุแล้ว",
        valid: "ยังใช้ได้",
        sendAlerts: "ส่งการแจ้งเตือน"
      },
      reports: {
        title: "รายงาน",
        generate: "สร้างรายงาน",
        download: "ดาวน์โหลด",
        workers: "รายงานแรงงาน",
        complaints: "รายงานการร้องเรียน",
        payments: "รายงานการชำระเงิน"
      }
    }
  },

  ur: {
    logout: "لاگ آؤٹ",
    signOut: "سائن آؤٹ",
    loading: "لوڈ ہو رہا ہے...",
    save: "محفوظ کریں",
    cancel: "منسوخ",
    submit: "جمع کریں",
    delete: "حذف کریں",
    edit: "ترمیم",
    view: "دیکھیں",
    search: "تلاش کریں",
    filter: "فلٹر",
    status: "حیثیت",
    actions: "اقدامات",
    noData: "کوئی ڈیٹا نہیں",

    member: {
      title: "MyShield",
      subtitle: "مزدور تحفظ پلیٹ فارم",
      nav: {
        profile: "پروفائل",
        complaints: "شکایات",
        payments: "ادائیگیاں",
        documents: "دستاویزات",
        messages: "پیغامات"
      },
      profile: {
        title: "میرا پروفائل",
        personalInfo: "ذاتی معلومات",
        fullName: "پورا نام",
        email: "ای میل",
        phone: "فون",
        passportNumber: "پاسپورٹ نمبر",
        nationality: "قومیت",
        employer: "آجر",
        visaExpiry: "ویزا ختم",
        subscriptionStatus: "رکنیت کی حیثیت",
        updateProfile: "پروفائل اپڈیٹ"
      },
      complaints: {
        title: "میری شکایات",
        newComplaint: "نئی شکایت",
        complaintTitle: "عنوان",
        description: "تفصیل",
        category: "زمرہ",
        priority: "ترجیح",
        submitComplaint: "شکایت جمع کریں",
        noComplaints: "ابھی تک کوئی شکایت نہیں"
      },
      payments: {
        title: "ادائیگی کی تاریخ",
        makePayment: "ادائیگی کریں",
        amount: "رقم",
        date: "تاریخ",
        method: "طریقہ",
        qrPayment: "QR کوڈ ادائیگی"
      },
      documents: {
        title: "میری دستاویزات",
        uploadDocument: "دستاویز اپلوڈ کریں",
        documentType: "دستاویز کی قسم",
        passport: "پاسپورٹ",
        visa: "ویزا/کام کا اجازت نامہ",
        contract: "ملازمت کا معاہدہ",
        other: "دیگر"
      },
      chat: {
        title: "پیغامات",
        sendMessage: "پیغام بھیجیں...",
        online: "آن لائن",
        offline: "آف لائن",
        lastSeen: "آخری بار دیکھا گیا"
      }
    },

    staff: {
      title: "MyShield عملہ",
      subtitle: "مزدور انتظام",
      nav: {
        myCases: "میرے کیسز",
        messages: "پیغامات"
      },
      cases: {
        title: "تفویض شدہ کیسز",
        worker: "مزدور",
        assignedDate: "تفویض کی تاریخ",
        noCases: "کوئی کیس تفویض نہیں"
      }
    },

    admin: {
      title: "ایڈمن پورٹل",
      nav: {
        dashboard: "ڈیش بورڈ",
        visaTracker: "ویزا ٹریکر",
        chatOversight: "چیٹ نگرانی",
        members: "اراکین",
        staffManagement: "عملے کا انتظام",
        reports: "رپورٹیں",
        analytics: "تجزیات"
      },
      dashboard: {
        welcome: "واپسی پر خوش آمدید",
        totalWorkers: "کل مزدور",
        activeComplaints: "فعال شکایات",
        totalStaff: "کل عملہ",
        monthlyRevenue: "ماہانہ آمدنی"
      },
      members: {
        title: "تمام اراکین",
        addMember: "رکن شامل کریں",
        viewProfile: "پروفائل دیکھیں"
      },
      visaTracker: {
        title: "ویزا اور اجازت نامہ ٹریکر",
        expiringSoon: "جلد ختم ہونے والا",
        expired: "ختم ہو گیا",
        valid: "درست",
        sendAlerts: "الرٹ بھیجیں"
      },
      reports: {
        title: "رپورٹیں",
        generate: "رپورٹ بنائیں",
        download: "ڈاؤن لوڈ",
        workers: "مزدور رپورٹ",
        complaints: "شکایات رپورٹ",
        payments: "ادائیگی رپورٹ"
      }
    }
  }
};

// Get saved language from localStorage
export const getSavedLanguage = () => {
  return localStorage.getItem('myshield_lang') || 'en';
};

// Save language to localStorage
export const saveLanguage = (lang) => {
  localStorage.setItem('myshield_lang', lang);
};

// Get translation helper
export const getTranslation = (lang, path) => {
  const keys = path.split('.');
  let result = dashboardTranslations[lang] || dashboardTranslations.en;
  
  for (const key of keys) {
    result = result?.[key];
    if (result === undefined) {
      // Fallback to English
      result = dashboardTranslations.en;
      for (const k of keys) {
        result = result?.[k];
      }
      break;
    }
  }
  
  return result || path;
};

// Custom hook for translations
export const useTranslation = () => {
  const lang = getSavedLanguage();
  
  const t = (path) => getTranslation(lang, path);
  
  return { t, lang };
};
